
import React, { useMemo, useState } from "react";

/*
  SchoolAdmissionEligibilitySections.js
  React + Tailwind CSS only
  13 unique Admission Process & Eligibility sections
  Accent Burgundy: #8B1E3F
*/

const cx = (...classes) => classes.filter(Boolean).join(" ");

const ArrowUpRight = ({ className = "h-4 w-4" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Check = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const FileText = ({ className = "h-6 w-6" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path d="M6 3h8l4 4v14H6V3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M14 3v5h5M9 12h6M9 16h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
);

const Badge = ({ children, dark = false }) => (
    <div className={cx(
        "inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.16em]",
        dark ? "text-[#D5AD57]" : "text-[#8B1E3F]"
    )}>
        <span className="h-px w-10 bg-current" />
        {children}
    </div>
);


const SectionEyebrow = ({ children }) => (
    <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-[0.22em] text-[#8B1E3F]">
        <span className="h-px w-10 bg-current" />{children}
    </div>
);
const ArrowIcon = ArrowUpRight;

const CTA = ({ href = "/apply-online", children = "Apply Online", light = false }) => (
    <a
        href={href}
        className={cx(
            "inline-flex min-h-[52px] items-center justify-center gap-3 rounded-full px-6 text-sm font-bold transition duration-300 hover:-translate-y-1",
            light ? "bg-white text-[#071A2F] hover:bg-[#D5AD57]" : "bg-[#8B1E3F] text-white hover:bg-[#071A2F]"
        )}
    >
        {children}
        <ArrowUpRight />
    </a>
);

const defaultSteps = [
    ["01", "Explore Programme", "Review course structure, duration, outcomes and available academic pathways."],
    ["02", "Submit Application", "Complete the online form using accurate personal and academic information."],
    ["03", "Document Review", "Upload required certificates and supporting documents for verification."],
    ["04", "Counselling", "Discuss programme suitability, eligibility and admission requirements."],
    ["05", "Confirm Admission", "Complete the applicable fee and enrolment formalities."],
    ["06", "Begin Orientation", "Join programme induction and start your academic journey."],
];

const documents = [
    "Academic mark sheets",
    "Qualifying examination certificate",
    "Government identity proof",
    "Passport-size photographs",
    "Transfer or migration certificate",
    "Category certificate, where applicable",
];

const eligibilityBase = {
    ug: "10+2 or equivalent from a recognised board, subject to programme requirements.",
    pg: "Relevant bachelor’s degree from a recognised university.",
    diploma: "Eligibility varies by diploma level and selected programme.",
    phd: "Relevant postgraduate qualification, entrance process and applicable research norms.",
};

/* 1. SCIENCE */
export function SchoolOfScienceAdmissionEligibility() {
    return (
        <section className="relative overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-28">
            <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,.3)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.3)_1px,transparent_1px)] [background-size:64px_64px]" />
            <div className="relative mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <header className="grid gap-8 border-b border-white/15 pb-12 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
                    <div>
                        <Badge dark>Admission Process & Eligibility</Badge>
                        <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04]">
                            Begin your journey into
                            <em className="text-[#D5AD57]"> scientific discovery.</em>
                        </h2>
                    </div>
                    <p className="text-base leading-8 text-white/65">
                        A clear admission pathway for undergraduate, postgraduate, diploma and doctoral science programmes.
                    </p>
                </header>

                <div className="mt-12 grid gap-6 lg:grid-cols-[1.15fr_.85fr]">
                    <div className="grid gap-4 sm:grid-cols-2">
                        {defaultSteps.map(([n, t, d]) => (
                            <article key={n} className="border border-white/15 bg-white/[0.04] p-6 transition hover:bg-white/[0.07]">
                                <span className="text-sm font-bold text-[#D5AD57]">{n}</span>
                                <h3 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal">{t}</h3>
                                <p className="mt-3 text-sm leading-7 text-white/60">{d}</p>
                            </article>
                        ))}
                    </div>
                    <aside className="bg-white p-7 text-[#132337] sm:p-9">
                        <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">Eligibility Matrix</span>
                        {[
                            ["Undergraduate", eligibilityBase.ug],
                            ["Postgraduate", eligibilityBase.pg],
                            ["Diploma", eligibilityBase.diploma],
                            ["Doctoral", eligibilityBase.phd],
                        ].map(([level, text]) => (
                            <div key={level} className="border-b border-[#071A2F]/15 py-5">
                                <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-2xl text-[#071A2F]">{level}</h3>
                                <p className="mt-2 text-sm leading-7 text-[#65717D]">{text}</p>
                            </div>
                        ))}
                        <div className="mt-7"><CTA /></div>
                    </aside>
                </div>
            </div>
        </section>
    );
}

/* 2. ARTS & HUMANITIES */
export function SchoolOfArtsHumanitiesAdmissionEligibility() {
    return (
        <section className="bg-[#F7F2E8] py-20 text-[#132337] sm:py-24 lg:py-28">
            <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <div className="grid gap-12 lg:grid-cols-[360px_1fr]">
                    <aside className="border-y-2 border-[#071A2F] py-10">
                        <Badge>Admission Journey</Badge>
                        <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,4.8vw,66px)] font-normal leading-[1.05] text-[#071A2F]">
                            Enter a world of
                            <em className="block text-[#8B1E3F]"> ideas and inquiry.</em>
                        </h2>
                        <p className="mt-6 text-base leading-8 text-[#65717D]">
                            Explore pathways in languages, social sciences, humanities, public service and research.
                        </p>
                        <div className="mt-8"><CTA>Start Application</CTA></div>
                    </aside>

                    <div>
                        <div className="grid border-l border-t border-[#071A2F]/15 sm:grid-cols-3">
                            {defaultSteps.map(([n, t, d]) => (
                                <article key={n} className="min-h-[190px] border-b border-r border-[#071A2F]/15 bg-white/60 p-6">
                                    <span className="text-sm font-bold text-[#A77B2D]">{n}</span>
                                    <h3 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-2xl text-[#071A2F]">{t}</h3>
                                    <p className="mt-3 text-sm leading-7 text-[#65717D]">{d}</p>
                                </article>
                            ))}
                        </div>
                        <div className="mt-8 grid gap-5 sm:grid-cols-2">
                            <div className="border-l-4 border-[#8B1E3F] bg-white p-6">
                                <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-3xl text-[#071A2F]">Undergraduate Entry</h3>
                                <p className="mt-3 text-sm leading-7 text-[#65717D]">{eligibilityBase.ug}</p>
                            </div>
                            <div className="border-l-4 border-[#D5AD57] bg-white p-6">
                                <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-3xl text-[#071A2F]">Postgraduate Entry</h3>
                                <p className="mt-3 text-sm leading-7 text-[#65717D]">{eligibilityBase.pg}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* 3. LAW */
export function SchoolOfLawAdmissionEligibility() {
    return (
        <section className="bg-[#EEF4F8] py-20 text-[#132337] sm:py-24 lg:py-28">
            <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <header className="border-b-[3px] border-[#071A2F] pb-10">
                    <Badge>Admission Process & Eligibility</Badge>
                    <h2 className="mt-6 max-w-5xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04] text-[#071A2F]">
                        Your route to a
                        <em className="text-[#8B1E3F]"> legal education.</em>
                    </h2>
                </header>

                <div className="mt-10 grid border-2 border-[#071A2F] lg:grid-cols-[1fr_380px]">
                    <div>
                        {defaultSteps.map(([n, t, d]) => (
                            <article key={n} className="grid gap-5 border-b border-[#071A2F]/15 p-6 sm:grid-cols-[80px_1fr]">
                                <span className="font-['Libre_Caslon_Display',Georgia,serif] text-4xl text-[#A77B2D]">{n}</span>
                                <div>
                                    <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-3xl text-[#071A2F]">{t}</h3>
                                    <p className="mt-3 text-sm leading-7 text-[#65717D]">{d}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                    <aside className="bg-[#071A2F] p-8 text-white">
                        <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#D5AD57]">Legal Programme Eligibility</span>
                        {[
                            ["LL.B.", "Bachelor’s degree or equivalent, subject to current norms."],
                            ["BBA LL.B. / BA LL.B.", "10+2 or equivalent from a recognised board."],
                            ["LL.M.", "Recognised law degree meeting prescribed requirements."],
                            ["Ph.D. in Law", eligibilityBase.phd],
                        ].map(([level, text]) => (
                            <div key={level} className="border-b border-white/15 py-5">
                                <h3 className="text-lg font-bold">{level}</h3>
                                <p className="mt-2 text-sm leading-7 text-white/60">{text}</p>
                            </div>
                        ))}
                        <div className="mt-8"><CTA light /></div>
                    </aside>
                </div>
            </div>
        </section>
    );
}

/* 4. PHARMACY */
export function SchoolOfPharmacyAdmissionEligibility() {
    const levels = [
        {
            code: "DP",
            name: "D.Pharm",
            level: "Diploma programme",
            eligibility:
                "10+2 with applicable science subjects under current University admission norms.",
            qualification: "10+2 or equivalent",
            duration: "Programme specific",
            guidance:
                "Applicants should review the prescribed science-subject combination and current minimum-mark requirements.",
        },
        {
            code: "BP",
            name: "B.Pharm",
            level: "Undergraduate programme",
            eligibility:
                "10+2 with Physics, Chemistry and the required subject combination from a recognised board.",
            qualification: "10+2 with science",
            duration: "Programme specific",
            guidance:
                "Programme admission remains subject to document verification and applicable University requirements.",
        },
        {
            code: "MP",
            name: "M.Pharm",
            level: "Postgraduate programme",
            eligibility:
                "A recognised B.Pharm qualification meeting the prescribed academic and admission criteria.",
            qualification: "Recognised B.Pharm",
            duration: "Programme specific",
            guidance:
                "Specialisation availability and additional selection requirements may vary by academic session.",
        },
        {
            code: "PhD",
            name: "Ph.D. in Pharmacy",
            level: "Doctoral programme",
            eligibility: eligibilityBase.phd,
            qualification: "Relevant postgraduate degree",
            duration: "As per current norms",
            guidance:
                "Admission may include an entrance process, research proposal, interview and applicable regulatory requirements.",
        },
    ];

    const admissionStages = [
        {
            number: "01",
            title: "Explore",
            text: "Review programme levels, specialisations and academic requirements.",
        },
        {
            number: "02",
            title: "Apply",
            text: "Submit accurate personal, academic and programme information.",
        },
        {
            number: "03",
            title: "Verify",
            text: "Provide certificates and supporting documents for review.",
        },
        {
            number: "04",
            title: "Confirm",
            text: "Complete the applicable admission and enrolment formalities.",
        },
    ];

    const documents = [
        "Academic mark sheets",
        "Qualifying certificate",
        "Government identity proof",
        "Recent photographs",
        "Transfer or migration certificate",
        "Category certificate, where applicable",
    ];

    const [active, setActive] = useState(0);
    const selected = levels[active];

    return (
        <section
            className="relative isolate overflow-hidden bg-[#EEF4F8] py-20 text-[#132337] sm:py-24 lg:py-28"
            aria-labelledby="pharmacy-admission-heading"
        >
            {/* Background */}
            <div
                className="pointer-events-none absolute inset-0 -z-10"
                aria-hidden="true"
            >
                <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full border-[80px] border-[#8B1E3F]/[0.035]" />

                <div className="absolute -bottom-52 -left-44 h-[520px] w-[520px] rounded-full bg-[#D5AD57]/15 blur-[120px]" />

                <div className="absolute inset-0 opacity-[0.025] [background-image:radial-gradient(#071A2F_1px,transparent_1px)] [background-size:30px_30px]" />

                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D5AD57] to-transparent" />
            </div>

            <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                {/* Introduction */}
                <header className="grid gap-9 border-b border-[#071A2F]/15 pb-11 lg:grid-cols-[1fr_0.55fr] lg:items-end">
                    <div>
                        <Badge>Admission process & eligibility</Badge>

                        <h2
                            id="pharmacy-admission-heading"
                            className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04] tracking-[-0.04em] text-[#071A2F]"
                        >
                            Your pathway into{" "}
                            <em className="font-normal text-[#8B1E3F]">
                                pharmaceutical science.
                            </em>
                        </h2>
                    </div>

                    <p className="border-l border-[#071A2F]/15 pl-7 text-base leading-8 text-[#65717D] max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                        Explore programme-specific entry requirements and follow a
                        clear admission process built around application accuracy,
                        document verification and academic guidance.
                    </p>
                </header>

                {/* Admission journey */}
                <div className="relative mt-14">
                    <div
                        className="absolute left-[8%] right-[8%] top-8 hidden h-px bg-[#071A2F]/15 lg:block"
                        aria-hidden="true"
                    />

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {admissionStages.map((stage, index) => (
                            <article
                                key={stage.number}
                                className="group relative min-h-[230px] overflow-hidden rounded-[8px_28px_8px_8px] border border-[#071A2F]/10 bg-white p-6 transition duration-300 hover:-translate-y-1 hover:border-[#D5AD57] hover:shadow-[0_20px_55px_rgba(7,26,47,0.08)]"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="relative z-10 grid h-16 w-16 place-items-center rounded-full border-[5px] border-[#F4E8EC] bg-[#071A2F] text-sm font-bold text-[#D5AD57] transition duration-300 group-hover:bg-[#8B1E3F] group-hover:text-white">
                                        {stage.number}
                                    </span>

                                    <span className="text-xl text-[#A77B2D]">
                                        {index < admissionStages.length - 1
                                            ? "→"
                                            : "✓"}
                                    </span>
                                </div>

                                <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal text-[#071A2F]">
                                    {stage.title}
                                </h3>

                                <p className="mt-3 text-sm leading-7 text-[#65717D]">
                                    {stage.text}
                                </p>

                                <span className="absolute inset-y-0 left-0 w-1 origin-bottom scale-y-0 bg-[#8B1E3F] transition-transform duration-300 group-hover:scale-y-100" />
                            </article>
                        ))}
                    </div>
                </div>

                {/* Programme eligibility interface */}
                <div className="mt-16 overflow-hidden rounded-[8px_38px_8px_8px] border border-[#071A2F]/10 bg-white shadow-[0_28px_85px_rgba(7,26,47,0.1)]">
                    <div className="grid lg:grid-cols-[340px_1fr]">
                        {/* Programme selector */}
                        <aside className="relative overflow-hidden bg-[#071A2F] p-6 text-white sm:p-8">
                            <div
                                className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(white_1px,transparent_1px)] [background-size:100%_44px]"
                                aria-hidden="true"
                            />

                            <div className="relative">
                                <span className="text-sm font-bold uppercase tracking-[0.15em] text-[#D5AD57]">
                                    Pharmacy programmes
                                </span>

                                <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal leading-tight">
                                    Select your academic pathway.
                                </h3>

                                <div
                                    className="mt-8 space-y-2"
                                    role="tablist"
                                    aria-label="Pharmacy programme levels"
                                >
                                    {levels.map((item, index) => {
                                        const isActive = active === index;

                                        return (
                                            <button
                                                key={item.code}
                                                type="button"
                                                role="tab"
                                                aria-selected={isActive}
                                                onClick={() => setActive(index)}
                                                className={`group flex min-h-[76px] w-full items-center gap-4 border px-4 text-left transition duration-300 ${isActive
                                                    ? "border-[#D5AD57] bg-[#D5AD57] text-[#071A2F]"
                                                    : "border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
                                                    }`}
                                            >
                                                <span
                                                    className={`grid h-11 min-w-11 place-items-center rounded-full px-2 text-sm font-bold ${isActive
                                                        ? "bg-[#071A2F] text-[#D5AD57]"
                                                        : "border border-white/20 text-[#D5AD57]"
                                                        }`}
                                                >
                                                    {item.code}
                                                </span>

                                                <span>
                                                    <small
                                                        className={`block text-sm font-bold uppercase tracking-[0.1em] ${isActive
                                                            ? "text-[#8B1E3F]"
                                                            : "text-[#D5AD57]"
                                                            }`}
                                                    >
                                                        {item.level}
                                                    </small>

                                                    <strong className="mt-1 block text-lg">
                                                        {item.name}
                                                    </strong>
                                                </span>

                                                <span
                                                    className={`ml-auto transition-transform duration-300 group-hover:translate-x-1 ${isActive
                                                        ? "text-[#8B1E3F]"
                                                        : "text-white/40"
                                                        }`}
                                                    aria-hidden="true"
                                                >
                                                    →
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </aside>

                        {/* Selected programme */}
                        <article
                            className="relative min-h-[600px] overflow-hidden p-7 sm:p-10 lg:p-12"
                            role="tabpanel"
                            aria-live="polite"
                            key={selected.code}
                        >
                            <span
                                className="pointer-events-none absolute -right-5 -top-12 select-none font-['Libre_Caslon_Display',Georgia,serif] text-[180px] leading-none text-[#8B1E3F]/[0.035]"
                                aria-hidden="true"
                            >
                                {selected.code}
                            </span>

                            <div className="relative flex h-full flex-col">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <span className="text-sm font-bold uppercase tracking-[0.15em] text-[#8B1E3F]">
                                        Selected pharmacy pathway
                                    </span>

                                    <span className="rounded-full bg-[#F4E8EC] px-4 py-2 text-sm font-bold text-[#8B1E3F]">
                                        {selected.level}
                                    </span>
                                </div>

                                <h3 className="mt-9 max-w-3xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(38px,4.5vw,62px)] font-normal leading-[1.08] text-[#071A2F]">
                                    {selected.name}
                                </h3>

                                <p className="mt-6 max-w-3xl text-base leading-8 text-[#65717D]">
                                    {selected.eligibility}
                                </p>

                                <dl className="mt-9 grid border-y border-[#071A2F]/15 sm:grid-cols-2">
                                    <div className="border-b border-[#071A2F]/15 py-6 sm:border-b-0 sm:border-r sm:pr-6">
                                        <dt className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">
                                            Entry qualification
                                        </dt>

                                        <dd className="mt-3 text-lg font-semibold leading-7 text-[#071A2F]">
                                            {selected.qualification}
                                        </dd>
                                    </div>

                                    <div className="py-6 sm:pl-6">
                                        <dt className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">
                                            Duration
                                        </dt>

                                        <dd className="mt-3 text-lg font-semibold text-[#071A2F]">
                                            {selected.duration}
                                        </dd>
                                    </div>
                                </dl>

                                <div className="mt-8 rounded-[8px_24px_8px_8px] border-l-4 border-[#D5AD57] bg-[#F7F2E8] p-6">
                                    <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">
                                        Admission guidance
                                    </span>

                                    <p className="mt-3 text-sm leading-7 text-[#65717D]">
                                        {selected.guidance}
                                    </p>
                                </div>

                                <div className="mt-auto flex flex-col gap-5 pt-9 sm:flex-row sm:items-center sm:justify-between">
                                    <p className="max-w-xl text-sm leading-7 text-[#132337]/55">
                                        Final eligibility remains subject to document
                                        verification and the University’s prevailing
                                        admission regulations.
                                    </p>

                                    <CTA />
                                </div>
                            </div>
                        </article>
                    </div>
                </div>

                {/* Documents and support */}
                <div className="mt-10 grid overflow-hidden rounded-[8px_30px_8px_8px] border border-[#071A2F]/10 bg-white lg:grid-cols-[1fr_330px]">
                    <div className="p-7 sm:p-9">
                        <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                            Application checklist
                        </span>

                        <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal text-[#071A2F]">
                            Keep the required documents ready.
                        </h3>

                        <div className="mt-7 grid gap-3 sm:grid-cols-2">
                            {documents.map((document, index) => (
                                <div
                                    key={document}
                                    className="flex min-h-[76px] items-center gap-4 border border-[#071A2F]/10 bg-[#F7F2E8]/60 p-4"
                                >
                                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#8B1E3F] text-sm font-bold text-white">
                                        0{index + 1}
                                    </span>

                                    <strong className="text-sm leading-6 text-[#071A2F]">
                                        {document}
                                    </strong>
                                </div>
                            ))}
                        </div>
                    </div>

                    <aside className="flex flex-col justify-between bg-[#8B1E3F] p-7 text-white sm:p-9">
                        <div>
                            <span className="grid h-14 w-14 place-items-center rounded-full border border-white/25 text-2xl text-[#D5AD57]">
                                ?
                            </span>

                            <span className="mt-8 block text-sm font-bold uppercase tracking-[0.14em] text-[#D5AD57]">
                                Admission assistance
                            </span>

                            <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal leading-tight">
                                Need help choosing your pharmacy programme?
                            </h3>

                            <p className="mt-5 text-sm leading-7 text-white/65">
                                Speak with the admissions team for programme,
                                eligibility and application guidance.
                            </p>
                        </div>

                        <a
                            href="/contact-us"
                            className="mt-9 inline-flex min-h-[52px] items-center justify-center gap-4 bg-white px-6 text-sm font-bold text-[#8B1E3F] transition hover:bg-[#D5AD57] hover:text-[#071A2F]"
                        >
                            Contact admissions
                            <span aria-hidden="true">↗</span>
                        </a>
                    </aside>
                </div>
            </div>
        </section>
    );
}

/* 5. DESIGN */
export function SchoolOfDesignAdmissionEligibility() {
    const programmeLevels = [
        {
            code: "UG",
            title: "Bachelor’s Programmes",
            label: "Undergraduate",
            programmes:
                "Fashion, interior, product, communication and related design pathways.",
            qualification: eligibilityBase.ug,
            portfolio:
                "A portfolio may be requested where applicable to the selected programme.",
            note: "Programme-specific subject and minimum-mark requirements may apply.",
        },
        {
            code: "PG",
            title: "Master’s Programmes",
            label: "Postgraduate",
            programmes:
                "Advanced interdisciplinary, strategic and specialist design pathways.",
            qualification: eligibilityBase.pg,
            portfolio:
                "Applicants may be invited to present prior academic, professional or creative work.",
            note: "The relevance of the qualifying degree may be considered during admission review.",
        },
        {
            code: "DP",
            title: "Diploma Programmes",
            label: "Diploma",
            programmes:
                "Focused programmes in creative practice, materials, visualisation and design skills.",
            qualification: eligibilityBase.diploma,
            portfolio:
                "Prior creative work can support the application but may not be compulsory for every programme.",
            note: "Eligibility varies according to the programme level and selected discipline.",
        },
        {
            code: "PhD",
            title: "Doctoral Programme",
            label: "Research",
            programmes:
                "Advanced research into design practice, culture, innovation and human-centred systems.",
            qualification: eligibilityBase.phd,
            portfolio:
                "A research proposal and evidence of relevant academic or professional work may be required.",
            note: "Admission remains subject to the applicable entrance, interview and research regulations.",
        },
    ];

    const admissionStages = [
        {
            number: "01",
            verb: "Discover",
            title: "Explore your design direction",
            text: "Review disciplines, programme structures, learning environments and possible creative careers.",
        },
        {
            number: "02",
            verb: "Prepare",
            title: "Build your application",
            text: "Complete the application form and organise the required academic and personal information.",
        },
        {
            number: "03",
            verb: "Express",
            title: "Share your creative perspective",
            text: "Present a portfolio, statement or relevant creative work where required by the programme.",
        },
        {
            number: "04",
            verb: "Interact",
            title: "Attend the selection process",
            text: "Participate in an interaction, portfolio discussion or assessment where applicable.",
        },
        {
            number: "05",
            verb: "Confirm",
            title: "Complete your admission",
            text: "Finish document verification, applicable fee payment and enrolment formalities.",
        },
    ];

    const portfolioGuidance = [
        "Observational drawings",
        "Process sketches",
        "Photography or visual studies",
        "Material experiments",
        "Digital or physical projects",
        "Personal creative work",
    ];

    const [activeLevel, setActiveLevel] = useState(0);
    const selectedLevel = programmeLevels[activeLevel];

    return (
        <section
            className="relative isolate overflow-hidden bg-[#EEF4F8] py-20 text-[#132337] sm:py-24 lg:py-28"
            aria-labelledby="design-admission-heading"
        >
            {/* Studio-grid background */}
            <div
                className="pointer-events-none absolute inset-0 -z-10"
                aria-hidden="true"
            >
                <div className="absolute inset-y-0 left-0 w-2 bg-[#8B1E3F]" />

                <div className="absolute inset-0 opacity-[0.025] [background-image:linear-gradient(#071A2F_1px,transparent_1px),linear-gradient(90deg,#071A2F_1px,transparent_1px)] [background-size:48px_48px]" />

                <div className="absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full border-[80px] border-[#D5AD57]/10" />

                <div className="absolute -bottom-56 -left-36 h-[500px] w-[500px] rounded-full bg-[#F4E8EC] blur-[100px]" />
            </div>

            <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                {/* Introduction */}
                <header className="grid gap-9 border-b-2 border-[#071A2F] pb-11 lg:grid-cols-[1fr_0.55fr] lg:items-end">
                    <div>
                        <Badge>Admission process & eligibility</Badge>

                        <h2
                            id="design-admission-heading"
                            className="mt-6 max-w-5xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04] tracking-[-0.045em] text-[#071A2F]"
                        >
                            Begin with curiosity.{" "}
                            <em className="font-normal text-[#8B1E3F]">
                                Progress through creative purpose.
                            </em>
                        </h2>
                    </div>

                    <p className="border-l border-[#071A2F]/15 pl-7 text-base leading-8 text-[#65717D] max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                        A guided admission experience for students ready to explore
                        ideas, communicate their creative perspective and begin a
                        professional design education.
                    </p>
                </header>

                {/* Design admission brief */}
                <div className="mt-14 grid overflow-hidden border border-[#071A2F]/15 lg:grid-cols-[340px_1fr]">
                    <aside className="relative flex min-h-[560px] flex-col overflow-hidden bg-[#071A2F] p-8 text-white sm:p-10">
                        <div
                            className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(white_1px,transparent_1px),linear-gradient(90deg,white_1px,transparent_1px)] [background-size:42px_42px]"
                            aria-hidden="true"
                        />

                        <div className="relative">
                            <span className="text-sm font-bold uppercase tracking-[0.16em] text-[#D5AD57]">
                                The admission brief
                            </span>

                            <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal leading-[1.15] sm:text-5xl">
                                Your first design challenge is choosing where to
                                begin.
                            </h3>

                            <p className="mt-6 text-base leading-8 text-white/65">
                                Explore each programme carefully and prepare an
                                application that reflects your interests, academic
                                background and creative potential.
                            </p>
                        </div>

                        <div className="relative mt-auto pt-12">
                            <div className="h-px bg-white/20" />

                            <div className="mt-7 flex items-center gap-5">
                                <span className="grid h-14 w-14 place-items-center rounded-full border border-[#D5AD57]/50 text-2xl text-[#D5AD57]">
                                    ✦
                                </span>

                                <div>
                                    <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#D5AD57]">
                                        Creative principle
                                    </span>

                                    <strong className="mt-2 block text-lg">
                                        Process matters as much as outcome.
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Admission stages */}
                    <div className="grid sm:grid-cols-2 xl:grid-cols-5">
                        {admissionStages.map((stage, index) => (
                            <article
                                key={stage.number}
                                className={`group relative min-h-[280px] overflow-hidden border-b border-r border-[#071A2F]/10 p-6 transition duration-300 hover:bg-[#F7F2E8] ${index === 2
                                    ? "bg-[#F4E8EC]"
                                    : index === 4
                                        ? "bg-[#F7F2E8]"
                                        : "bg-white"
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-bold text-[#8B1E3F]">
                                        {stage.number}
                                    </span>

                                    <span className="text-lg text-[#A77B2D] transition-transform duration-300 group-hover:translate-x-1">
                                        {index < admissionStages.length - 1
                                            ? "→"
                                            : "✓"}
                                    </span>
                                </div>

                                <span className="mt-8 block text-sm font-bold uppercase tracking-[0.12em] text-[#A77B2D]">
                                    {stage.verb}
                                </span>

                                <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-8 text-[#071A2F]">
                                    {stage.title}
                                </h3>

                                <p className="mt-4 text-sm leading-7 text-[#65717D]">
                                    {stage.text}
                                </p>

                                <span className="absolute bottom-0 left-0 h-1 w-0 bg-[#8B1E3F] transition-all duration-300 group-hover:w-full" />
                            </article>
                        ))}
                    </div>
                </div>

                {/* Programme eligibility */}
                <div className="mt-20">
                    <div className="grid gap-8 lg:grid-cols-[1fr_0.58fr] lg:items-end">
                        <div>
                            <span className="text-sm font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                Programme eligibility
                            </span>

                            <h3 className="mt-4 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(36px,4.5vw,58px)] font-normal leading-[1.08] text-[#071A2F]">
                                Select the pathway that matches your{" "}
                                <em className="font-normal text-[#8B1E3F]">
                                    academic and creative direction.
                                </em>
                            </h3>
                        </div>

                        <p className="text-base leading-8 text-[#65717D]">
                            Entry requirements vary by academic level and programme.
                            Review the guidance before submitting your application.
                        </p>
                    </div>

                    <div className="mt-10 overflow-hidden border border-[#071A2F]/15 bg-white">
                        {/* Level navigation */}
                        <div
                            className="overflow-x-auto border-b border-[#071A2F]/15"
                            role="tablist"
                            aria-label="Design programme levels"
                        >
                            <div className="flex min-w-[720px]">
                                {programmeLevels.map((item, index) => {
                                    const isActive = activeLevel === index;

                                    return (
                                        <button
                                            key={item.code}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            onClick={() => setActiveLevel(index)}
                                            className={`group relative min-h-[110px] flex-1 border-r px-5 text-left transition duration-300 last:border-r-0 ${isActive
                                                ? "bg-[#8B1E3F] text-white"
                                                : "bg-white text-[#071A2F] hover:bg-[#F7F2E8]"
                                                }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span
                                                    className={`grid h-11 min-w-11 place-items-center px-2 text-sm font-bold ${isActive
                                                        ? "bg-[#D5AD57] text-[#071A2F]"
                                                        : "bg-[#F4E8EC] text-[#8B1E3F]"
                                                        }`}
                                                >
                                                    {item.code}
                                                </span>

                                                <span
                                                    className={`transition-transform duration-300 group-hover:translate-x-1 ${isActive
                                                        ? "text-[#D5AD57]"
                                                        : "text-[#A77B2D]"
                                                        }`}
                                                >
                                                    →
                                                </span>
                                            </div>

                                            <strong className="mt-4 block text-base">
                                                {item.label}
                                            </strong>

                                            {isActive && (
                                                <span className="absolute inset-x-0 bottom-0 h-1 bg-[#D5AD57]" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Selected level */}
                        <article
                            className="grid min-h-[520px] lg:grid-cols-[0.72fr_1.28fr]"
                            role="tabpanel"
                            aria-live="polite"
                            key={selectedLevel.code}
                        >
                            <div className="relative overflow-hidden bg-[#F4E8EC] p-7 sm:p-10">
                                <div
                                    className="absolute inset-0 opacity-[0.035] [background-image:linear-gradient(#071A2F_1px,transparent_1px),linear-gradient(90deg,#071A2F_1px,transparent_1px)] [background-size:38px_38px]"
                                    aria-hidden="true"
                                />

                                <span className="pointer-events-none absolute -bottom-8 -right-4 font-['Libre_Caslon_Display',Georgia,serif] text-[150px] leading-none text-[#8B1E3F]/[0.055]">
                                    {selectedLevel.code}
                                </span>

                                <div className="relative flex h-full flex-col">
                                    <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                                        Selected design pathway
                                    </span>

                                    <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal leading-[1.1] text-[#071A2F] sm:text-5xl">
                                        {selectedLevel.title}
                                    </h3>

                                    <p className="mt-6 text-base leading-8 text-[#65717D]">
                                        {selectedLevel.programmes}
                                    </p>

                                    <div className="mt-auto border-t border-[#071A2F]/15 pt-7">
                                        <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">
                                            Creative submission
                                        </span>

                                        <p className="mt-3 text-sm leading-7 text-[#65717D]">
                                            {selectedLevel.portfolio}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col p-7 sm:p-10 lg:p-12">
                                <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                                    Minimum qualification
                                </span>

                                <p className="mt-5 max-w-3xl font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-10 text-[#071A2F] sm:text-3xl">
                                    {selectedLevel.qualification}
                                </p>

                                <div className="mt-8 border-l-4 border-[#D5AD57] bg-[#F7F2E8] p-6">
                                    <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">
                                        Important guidance
                                    </span>

                                    <p className="mt-3 text-sm leading-7 text-[#65717D]">
                                        {selectedLevel.note}
                                    </p>
                                </div>

                                <div className="mt-auto flex flex-col gap-5 pt-10 sm:flex-row sm:items-center sm:justify-between">
                                    <p className="max-w-xl text-sm leading-7 text-[#132337]/55">
                                        Final eligibility remains subject to
                                        verification and the University’s current
                                        admission regulations.
                                    </p>

                                    <CTA>Begin Application</CTA>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>

                {/* Portfolio guidance */}
                <div className="mt-12 grid overflow-hidden rounded-[8px_34px_8px_8px] bg-[#071A2F] text-white lg:grid-cols-[340px_1fr]">
                    <div className="border-b border-white/15 p-7 sm:p-9 lg:border-b-0 lg:border-r">
                        <span className="text-sm font-bold uppercase tracking-[0.15em] text-[#D5AD57]">
                            Portfolio guidance
                        </span>

                        <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal leading-tight">
                            Show how you observe, explore and develop ideas.
                        </h3>

                        <p className="mt-5 text-sm leading-7 text-white/60">
                            Where a portfolio is requested, focus on authentic work
                            that communicates curiosity and process.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3">
                        {portfolioGuidance.map((item, index) => (
                            <div
                                key={item}
                                className="group flex min-h-[130px] items-center gap-4 border-b border-r border-white/15 p-5 transition hover:bg-white/[0.06]"
                            >
                                <span className="grid h-10 w-10 shrink-0 place-items-center border border-[#D5AD57]/40 text-sm font-bold text-[#D5AD57]">
                                    0{index + 1}
                                </span>

                                <strong className="text-sm leading-6 text-white/80">
                                    {item}
                                </strong>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Admission assistance */}
                <div className="mt-10 flex flex-col gap-7 border border-[#071A2F]/15 bg-[#F7F2E8] p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
                    <div className="max-w-3xl">
                        <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                            Need admission guidance?
                        </span>

                        <p className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-9 text-[#071A2F] sm:text-3xl">
                            Speak with the admissions team about programme selection,
                            eligibility or portfolio requirements.
                        </p>
                    </div>

                    <a
                        href="/contact-us"
                        className="inline-flex min-h-[54px] shrink-0 items-center justify-center gap-4 bg-[#8B1E3F] px-7 text-sm font-bold text-white transition duration-300 hover:-translate-y-1 hover:bg-[#071A2F]"
                    >
                        Contact admissions
                        <span aria-hidden="true">↗</span>
                    </a>
                </div>
            </div>
        </section>
    );
}

/* 6. EDUCATION */
export function SchoolOfEducationAdmissionEligibility() {
    return (
        <section className="bg-[#EEE5D3] py-20 text-[#132337] sm:py-24 lg:py-28">
            <div className="mx-auto w-[min(1280px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <header className="mx-auto max-w-4xl text-center">
                    <Badge>Admission Process & Eligibility</Badge>
                    <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.04] text-[#071A2F]">
                        Begin your path to
                        <em className="text-[#8B1E3F]"> becoming an educator.</em>
                    </h2>
                </header>

                <div className="relative mt-14">
                    <div className="absolute left-1/2 top-0 hidden h-full w-px bg-[#071A2F]/20 lg:block" />
                    {defaultSteps.map(([n, t, d], index) => (
                        <div key={n} className="mb-6 grid lg:grid-cols-2">
                            <article className={cx(
                                "rounded-[24px] bg-white p-7 shadow-[0_18px_50px_rgba(7,26,47,.07)]",
                                index % 2 ? "lg:col-start-2 lg:ml-10" : "lg:mr-10"
                            )}>
                                <span className="text-sm font-bold text-[#8B1E3F]">{n}</span>
                                <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-3xl text-[#071A2F]">{t}</h3>
                                <p className="mt-3 text-sm leading-7 text-[#65717D]">{d}</p>
                            </article>
                        </div>
                    ))}
                </div>

                <div className="mt-10 rounded-[24px] bg-[#071A2F] p-8 text-white">
                    <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-3xl">Eligibility Guidance</h3>
                    <div className="mt-6 grid gap-5 sm:grid-cols-3">
                        {[
                            ["BA (Hons.) Education", eligibilityBase.ug],
                            ["MA Education", eligibilityBase.pg],
                            ["Ph.D. Education", eligibilityBase.phd],
                        ].map(([title, text]) => (
                            <div key={title} className="border-l border-white/20 pl-5">
                                <h4 className="text-lg font-bold text-[#D5AD57]">{title}</h4>
                                <p className="mt-2 text-sm leading-7 text-white/60">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* 7. JOURNALISM */
export function SchoolOfJournalismMassCommunicationAdmissionEligibility() {
    return (
        <section className="relative overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-28">
            <div className="absolute inset-0 opacity-[0.04] [background-image:repeating-linear-gradient(0deg,transparent,transparent_47px,rgba(255,255,255,.2)_48px)]" />
            <div className="relative mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <header className="grid gap-8 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
                    <div>
                        <Badge dark>Admission Process & Eligibility</Badge>
                        <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,70px)] font-normal leading-[1.04]">
                            Your first step into
                            <em className="text-[#D5AD57]"> media and storytelling.</em>
                        </h2>
                    </div>
                    <p className="text-base leading-8 text-white/65">
                        A transparent process for students pursuing journalism, broadcasting, digital media and communication.
                    </p>
                </header>

                <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
                    {defaultSteps.map(([n, t, d]) => (
                        <article key={n} className="min-h-[240px] border-t-4 border-[#8B1E3F] bg-white/[0.05] p-6">
                            <span className="text-sm font-bold text-[#D5AD57]">{n}</span>
                            <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-2xl">{t}</h3>
                            <p className="mt-3 text-sm leading-7 text-white/60">{d}</p>
                        </article>
                    ))}
                </div>

                <div className="mt-10 grid gap-5 lg:grid-cols-[1fr_320px]">
                    <div className="grid gap-5 sm:grid-cols-3">
                        {[
                            ["BA JMC", eligibilityBase.ug],
                            ["MA JMC", eligibilityBase.pg],
                            ["Diploma JMC", eligibilityBase.diploma],
                        ].map(([title, text]) => (
                            <div key={title} className="bg-white p-6 text-[#132337]">
                                <h3 className="text-xl font-bold text-[#071A2F]">{title}</h3>
                                <p className="mt-3 text-sm leading-7 text-[#65717D]">{text}</p>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-center bg-[#8B1E3F] p-7"><CTA light /></div>
                </div>
            </div>
        </section>
    );
}

/* 8. MEDIA & ENTERTAINMENT */
export function SchoolOfMediaEntertainmentAdmissionEligibility() {
    const admissionStages = [
        ["01", "Discover", "Choose your creative direction", "Explore programmes, production disciplines and possible media careers."],
        ["02", "Apply", "Submit your application", "Complete the application with accurate personal and academic information."],
        ["03", "Create", "Present your creative potential", "Share a portfolio, audition, showreel or creative statement where required."],
        ["04", "Interact", "Attend the selection process", "Participate in counselling, an interview or a creative assessment where applicable."],
        ["05", "Verify", "Complete document review", "Provide certificates, identification and supporting documents for verification."],
        ["06", "Begin", "Confirm your admission", "Complete enrolment formalities and prepare to join the creative community."],
    ];

    const programmeLevels = [
        {
            code: "UG",
            title: "Undergraduate",
            label: "Bachelor’s programmes",
            programmes:
                "Film, television, animation, performance, digital media and creative-production pathways.",
            qualification: eligibilityBase.ug,
            selection:
                "A portfolio review, creative interaction or audition may apply to selected programmes.",
        },
        {
            code: "PG",
            title: "Postgraduate",
            label: "Master’s programmes",
            programmes:
                "Advanced pathways in media practice, production, storytelling and creative leadership.",
            qualification: eligibilityBase.pg,
            selection:
                "Prior academic, professional or creative work may be considered during programme review.",
        },
        {
            code: "DP",
            title: "Diploma",
            label: "Diploma programmes",
            programmes:
                "Focused pathways in production, performance, content creation and technical media skills.",
            qualification: eligibilityBase.diploma,
            selection:
                "Programme-specific academic or creative requirements may apply.",
        },
        {
            code: "PhD",
            title: "Doctoral",
            label: "Research programmes",
            programmes:
                "Advanced inquiry into media, entertainment, audiences, culture and the creative industries.",
            qualification: eligibilityBase.phd,
            selection:
                "An entrance process, research proposal and interview may form part of admission.",
        },
    ];

    const creativeSubmissions = [
        ["PF", "Portfolio", "Visual, written or multidisciplinary creative work."],
        ["SR", "Showreel", "Selected video, animation, performance or production work."],
        ["AU", "Audition", "A prepared performance where required by the programme."],
        ["CS", "Creative statement", "Your interests, influences and professional ambitions."],
    ];

    const [activeLevel, setActiveLevel] = useState(0);
    const selectedLevel = programmeLevels[activeLevel];

    return (
        <section
            className="relative isolate overflow-hidden bg-[#F4E8EC] py-20 text-[#132337] sm:py-24 lg:py-28"
            aria-labelledby="media-admission-heading"
        >
            {/* Background */}
            <div
                className="pointer-events-none absolute inset-0 -z-10"
                aria-hidden="true"
            >
                <div className="absolute -right-48 -top-48 h-[600px] w-[600px] rounded-full border-[90px] border-[#8B1E3F]/[0.035]" />

                <div className="absolute -bottom-48 -left-40 h-[520px] w-[520px] rounded-full bg-[#D5AD57]/15 blur-[120px]" />

                <div className="absolute inset-0 opacity-[0.025] [background-image:linear-gradient(90deg,#071A2F_1px,transparent_1px)] [background-size:120px_100%]" />

                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D5AD57] to-transparent" />
            </div>

            <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                {/* Heading */}
                <header className="grid gap-9 border-b-2 border-[#071A2F] pb-11 lg:grid-cols-[1fr_0.55fr] lg:items-end">
                    <div>
                        <Badge>Admission process & eligibility</Badge>

                        <h2
                            id="media-admission-heading"
                            className="mt-6 max-w-5xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04] tracking-[-0.045em] text-[#071A2F]"
                        >
                            Turn creative ambition into{" "}
                            <em className="font-normal text-[#8B1E3F]">
                                your professional beginning.
                            </em>
                        </h2>
                    </div>

                    <p className="border-l border-[#071A2F]/15 pl-7 text-base leading-8 text-[#65717D] max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                        A guided admission pathway for aspiring storytellers,
                        performers, producers and media creators ready to begin
                        professional creative education.
                    </p>
                </header>

                {/* Admission stages */}
                <div className="mt-14 overflow-hidden rounded-[8px_38px_8px_8px] border border-[#071A2F]/10 bg-white shadow-[0_28px_80px_rgba(7,26,47,0.09)]">
                    <div className="grid lg:grid-cols-[330px_1fr]">
                        <aside className="relative flex min-h-[560px] flex-col overflow-hidden bg-[#071A2F] p-8 text-white sm:p-10">
                            <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(white_1px,transparent_1px),linear-gradient(90deg,white_1px,transparent_1px)] [background-size:44px_44px]" />

                            <div className="relative">
                                <span className="text-sm font-bold uppercase tracking-[0.15em] text-[#D5AD57]">
                                    Admission production plan
                                </span>

                                <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal leading-[1.15]">
                                    Six stages from first idea to first day.
                                </h3>

                                <p className="mt-6 text-base leading-8 text-white/60">
                                    Follow a structured journey through programme
                                    discovery, creative assessment and admission
                                    confirmation.
                                </p>
                            </div>

                            <div className="relative mt-auto border-t border-white/15 pt-7">
                                <span className="text-sm font-bold uppercase tracking-[0.13em] text-[#D5AD57]">
                                    Creative direction
                                </span>

                                <p className="mt-3 text-xl font-semibold leading-8">
                                    Imagine. Prepare. Present. Begin.
                                </p>
                            </div>
                        </aside>

                        <div className="grid sm:grid-cols-2 xl:grid-cols-3">
                            {admissionStages.map(
                                ([number, label, title, text], index) => (
                                    <article
                                        key={number}
                                        className={`group relative min-h-[280px] overflow-hidden border-b border-r border-[#071A2F]/10 p-6 transition duration-300 hover:bg-[#F7F2E8] sm:p-7 ${index === 0
                                                ? "bg-[#8B1E3F] text-white"
                                                : index === 4
                                                    ? "bg-[#F7F2E8] text-[#071A2F]"
                                                    : "bg-white text-[#071A2F]"
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span
                                                className={`grid h-11 w-11 place-items-center rounded-full text-sm font-bold ${index === 0
                                                        ? "border border-white/25 text-[#D5AD57]"
                                                        : "bg-[#F4E8EC] text-[#8B1E3F]"
                                                    }`}
                                            >
                                                {number}
                                            </span>

                                            <span
                                                className={`text-sm font-bold uppercase tracking-[0.1em] ${index === 0
                                                        ? "text-[#D5AD57]"
                                                        : "text-[#A77B2D]"
                                                    }`}
                                            >
                                                {label}
                                            </span>
                                        </div>

                                        <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-8">
                                            {title}
                                        </h3>

                                        <p
                                            className={`mt-4 text-sm leading-7 ${index === 0
                                                    ? "text-white/60"
                                                    : "text-[#65717D]"
                                                }`}
                                        >
                                            {text}
                                        </p>

                                        <span
                                            className={`pointer-events-none absolute -bottom-5 -right-2 font-['Libre_Caslon_Display',Georgia,serif] text-[96px] leading-none ${index === 0
                                                    ? "text-white/[0.04]"
                                                    : "text-[#071A2F]/[0.035]"
                                                }`}
                                        >
                                            {number}
                                        </span>

                                        <span className="absolute bottom-0 left-0 h-1 w-0 bg-[#D5AD57] transition-all duration-300 group-hover:w-full" />
                                    </article>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* Programme eligibility */}
                <div className="mt-20">
                    <div className="grid gap-8 lg:grid-cols-[1fr_0.58fr] lg:items-end">
                        <div>
                            <span className="text-sm font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                Programme eligibility
                            </span>

                            <h3 className="mt-4 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(36px,4.5vw,58px)] font-normal leading-[1.08] text-[#071A2F]">
                                Select the pathway that matches your{" "}
                                <em className="font-normal text-[#8B1E3F]">
                                    creative direction.
                                </em>
                            </h3>
                        </div>

                        <p className="text-base leading-8 text-[#65717D]">
                            Review indicative academic requirements and any creative
                            selection guidance for your preferred programme level.
                        </p>
                    </div>

                    <div className="mt-10 overflow-hidden rounded-[8px_38px_8px_8px] border border-[#071A2F]/10 bg-white shadow-[0_28px_80px_rgba(7,26,47,0.08)]">
                        {/* Level tabs */}
                        <div
                            className="overflow-x-auto border-b border-[#071A2F]/10"
                            role="tablist"
                            aria-label="Media programme levels"
                        >
                            <div className="flex min-w-[720px]">
                                {programmeLevels.map((level, index) => {
                                    const isActive = activeLevel === index;

                                    return (
                                        <button
                                            key={level.code}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            onClick={() => setActiveLevel(index)}
                                            className={`group relative min-h-[112px] flex-1 border-r px-5 text-left transition last:border-r-0 ${isActive
                                                    ? "bg-[#8B1E3F] text-white"
                                                    : "bg-white text-[#071A2F] hover:bg-[#F7F2E8]"
                                                }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span
                                                    className={`grid h-11 min-w-11 place-items-center px-2 text-sm font-bold ${isActive
                                                            ? "bg-[#D5AD57] text-[#071A2F]"
                                                            : "bg-[#F4E8EC] text-[#8B1E3F]"
                                                        }`}
                                                >
                                                    {level.code}
                                                </span>

                                                <span
                                                    className={`transition-transform group-hover:translate-x-1 ${isActive
                                                            ? "text-[#D5AD57]"
                                                            : "text-[#A77B2D]"
                                                        }`}
                                                >
                                                    →
                                                </span>
                                            </div>

                                            <strong className="mt-4 block text-base">
                                                {level.title}
                                            </strong>

                                            {isActive && (
                                                <span className="absolute inset-x-0 bottom-0 h-1 bg-[#D5AD57]" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Active eligibility */}
                        <article
                            className="grid min-h-[540px] lg:grid-cols-[0.72fr_1.28fr]"
                            role="tabpanel"
                            aria-live="polite"
                            key={selectedLevel.code}
                        >
                            <div className="relative overflow-hidden bg-[#071A2F] p-7 text-white sm:p-10">
                                <span className="pointer-events-none absolute -bottom-8 -right-3 font-['Libre_Caslon_Display',Georgia,serif] text-[160px] leading-none text-white/[0.04]">
                                    {selectedLevel.code}
                                </span>

                                <div className="relative flex h-full flex-col">
                                    <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#D5AD57]">
                                        Selected pathway
                                    </span>

                                    <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal leading-[1.1] sm:text-5xl">
                                        {selectedLevel.label}
                                    </h3>

                                    <p className="mt-6 text-base leading-8 text-white/60">
                                        {selectedLevel.programmes}
                                    </p>

                                    <div className="mt-auto border-t border-white/15 pt-7">
                                        <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#D5AD57]">
                                            Creative selection
                                        </span>

                                        <p className="mt-3 text-sm leading-7 text-white/60">
                                            {selectedLevel.selection}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col p-7 sm:p-10 lg:p-12">
                                <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                                    Minimum qualification
                                </span>

                                <p className="mt-5 max-w-3xl font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-10 text-[#071A2F] sm:text-3xl">
                                    {selectedLevel.qualification}
                                </p>

                                <div className="mt-8 border-l-4 border-[#D5AD57] bg-[#F7F2E8] p-6">
                                    <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">
                                        Admission guidance
                                    </span>

                                    <p className="mt-3 text-sm leading-7 text-[#65717D]">
                                        Programme-specific requirements may apply.
                                        Applicants should review the current admission
                                        guidance before submitting their application.
                                    </p>
                                </div>

                                <div className="mt-auto flex flex-col gap-5 pt-10 sm:flex-row sm:items-center sm:justify-between">
                                    <p className="max-w-xl text-sm leading-7 text-[#132337]/55">
                                        Final eligibility remains subject to
                                        verification and current University
                                        regulations.
                                    </p>

                                    <CTA>Begin Application</CTA>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>

                {/* Creative submission formats */}
                <div className="mt-12 grid overflow-hidden border border-[#071A2F]/10 bg-white sm:grid-cols-2 lg:grid-cols-4">
                    {creativeSubmissions.map(([code, title, text], index) => (
                        <article
                            key={code}
                            className="group relative min-h-[230px] border-b border-r border-[#071A2F]/10 p-6 transition hover:bg-[#F7F2E8]"
                        >
                            <div className="flex items-center justify-between">
                                <span className="grid h-12 min-w-12 place-items-center bg-[#071A2F] px-2 text-sm font-bold text-[#D5AD57]">
                                    {code}
                                </span>

                                <span className="text-sm font-bold text-[#8B1E3F]">
                                    0{index + 1}
                                </span>
                            </div>

                            <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal text-[#071A2F]">
                                {title}
                            </h3>

                            <p className="mt-4 text-sm leading-7 text-[#65717D]">
                                {text}
                            </p>

                            <span className="absolute bottom-0 left-0 h-1 w-0 bg-[#8B1E3F] transition-all duration-300 group-hover:w-full" />
                        </article>
                    ))}
                </div>

                {/* Final CTA */}
                <div className="mt-10 flex flex-col gap-7 rounded-[8px_30px_8px_8px] bg-[#8B1E3F] p-7 text-white sm:p-9 lg:flex-row lg:items-center lg:justify-between">
                    <div className="max-w-3xl">
                        <span className="text-sm font-bold uppercase tracking-[0.15em] text-[#D5AD57]">
                            Ready for your opening scene?
                        </span>

                        <p className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-9 sm:text-3xl">
                            Start your application and begin building your creative
                            future.
                        </p>
                    </div>

                    <a
                        href="/apply-online"
                        className="inline-flex min-h-[54px] shrink-0 items-center justify-center gap-4 bg-white px-7 text-sm font-bold text-[#8B1E3F] transition hover:-translate-y-1 hover:bg-[#D5AD57] hover:text-[#071A2F]"
                    >
                        Apply online
                        <span aria-hidden="true">↗</span>
                    </a>
                </div>
            </div>
        </section>
    );
}

/* 9. LIBRARY & INFORMATION SCIENCE */
export function SchoolOfLibraryInformationScienceAdmissionEligibility() {
    return (
        <section className="bg-[#F7F2E8] py-20 text-[#132337] sm:py-24 lg:py-28">
            <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <div className="grid border border-[#071A2F]/15 lg:grid-cols-[360px_1fr]">
                    <aside className="bg-[#071A2F] p-8 text-white sm:p-10">
                        <FileText className="h-10 w-10 text-[#D5AD57]" />
                        <Badge dark>Admission Process</Badge>
                        <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.7vw,64px)] font-normal leading-[1.05]">
                            Access your future in
                            <em className="block text-[#D5AD57]"> knowledge services.</em>
                        </h2>
                    </aside>

                    <div className="p-7 sm:p-10">
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {defaultSteps.map(([n, t]) => (
                                <div key={n} className="border-t-4 border-[#8B1E3F] bg-white p-5 shadow-[0_14px_35px_rgba(7,26,47,.06)]">
                                    <span className="text-sm font-bold text-[#A77B2D]">{n}</span>
                                    <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-2xl text-[#071A2F]">{t}</h3>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8 grid gap-5 sm:grid-cols-3">
                            {[
                                ["B.Lib. & I.Sc.", "Bachelor’s degree or equivalent, subject to current norms."],
                                ["M.Lib. & I.Sc.", "Relevant bachelor’s qualification under applicable regulations."],
                                ["Ph.D.", eligibilityBase.phd],
                            ].map(([title, text]) => (
                                <div key={title} className="border-l-2 border-[#D5AD57] pl-5">
                                    <h3 className="text-lg font-bold text-[#071A2F]">{title}</h3>
                                    <p className="mt-2 text-sm leading-7 text-[#65717D]">{text}</p>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8"><CTA /></div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* 10. YOGA & NATUROPATHY */
export function SchoolOfYogaNaturopathyAdmissionEligibility() {
    return (
        <section className="relative overflow-hidden bg-[#F7F2E8] py-20 text-[#132337] sm:py-24 lg:py-28">
            <div className="absolute left-1/2 top-1/2 h-[620px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#8B1E3F]/10" />
            <div className="relative mx-auto w-[min(1280px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <header className="mx-auto max-w-4xl text-center">
                    <Badge>Admission Process & Eligibility</Badge>
                    <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.04] text-[#071A2F]">
                        Begin a journey of
                        <em className="text-[#8B1E3F]"> wellbeing and purpose.</em>
                    </h2>
                </header>

                <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {defaultSteps.map(([n, t, d]) => (
                        <article key={n} className="rounded-full border border-[#071A2F]/10 bg-white/75 p-8 text-center shadow-[0_18px_45px_rgba(7,26,47,.07)]">
                            <span className="text-sm font-bold text-[#A77B2D]">{n}</span>
                            <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-2xl text-[#071A2F]">{t}</h3>
                            <p className="mt-3 text-sm leading-7 text-[#65717D]">{d}</p>
                        </article>
                    ))}
                </div>

                <div className="mt-10 grid gap-5 sm:grid-cols-4">
                    {[
                        ["Bachelor’s", eligibilityBase.ug],
                        ["Master’s", eligibilityBase.pg],
                        ["Diploma", eligibilityBase.diploma],
                        ["Doctoral", eligibilityBase.phd],
                    ].map(([title, text]) => (
                        <div key={title} className="border-t-2 border-[#8B1E3F] bg-white p-5">
                            <h3 className="text-lg font-bold text-[#071A2F]">{title}</h3>
                            <p className="mt-2 text-sm leading-7 text-[#65717D]">{text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* 11. LIFE SCIENCE */
export function SchoolOfLifeScienceAdmissionEligibility() {
    return (
        <section className="relative overflow-hidden bg-white py-20 text-[#132337] sm:py-24 lg:py-28">
            <div className="absolute -left-28 top-10 h-72 w-72 rounded-full bg-[#D5AD57]/10 blur-3xl" />
            <div className="absolute -right-28 bottom-0 h-80 w-80 rounded-full bg-[#8B1E3F]/10 blur-3xl" />
            <div className="relative mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <header className="grid gap-8 lg:grid-cols-[1.1fr_.9fr] lg:items-end">
                    <div>
                        <Badge>Admission Process & Eligibility</Badge>
                        <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04] text-[#071A2F]">
                            Start your journey into
                            <em className="text-[#8B1E3F]"> life and discovery.</em>
                        </h2>
                    </div>
                    <p className="border-l border-[#071A2F]/15 pl-6 text-base leading-8 text-[#65717D]">
                        Explore biotechnology, microbiology and research-oriented pathways through a simple admission process.
                    </p>
                </header>

                <div className="mt-12 grid gap-5 lg:grid-cols-[360px_1fr]">
                    <aside className="rounded-[30px] bg-[#071A2F] p-8 text-white">
                        <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-4xl">Eligibility Snapshot</h3>
                        {[
                            ["B.Sc. (Hons.)", eligibilityBase.ug],
                            ["M.Sc.", eligibilityBase.pg],
                            ["Ph.D.", eligibilityBase.phd],
                        ].map(([title, text]) => (
                            <div key={title} className="border-b border-white/15 py-5">
                                <h4 className="text-lg font-bold text-[#D5AD57]">{title}</h4>
                                <p className="mt-2 text-sm leading-7 text-white/60">{text}</p>
                            </div>
                        ))}
                        <div className="mt-7"><CTA light /></div>
                    </aside>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {defaultSteps.map(([n, t, d]) => (
                            <article key={n} className="rounded-[24px] border border-[#071A2F]/10 bg-[#F7F2E8] p-6">
                                <span className="grid h-10 w-10 place-items-center rounded-full bg-[#8B1E3F] text-sm font-bold text-white">{n}</span>
                                <h3 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-2xl text-[#071A2F]">{t}</h3>
                                <p className="mt-3 text-sm leading-7 text-[#65717D]">{d}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* 12. VOCATIONAL MANAGEMENT */
export function SchoolOfVocationalManagementAdmissionEligibility() {
    return (
        <section className="relative overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-28">
            <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(139,30,63,.22),transparent_35%,rgba(213,173,87,.08))]" />
            <div className="relative mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <header className="grid gap-8 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
                    <div>
                        <Badge dark>Admission Process & Eligibility</Badge>
                        <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04]">
                            Choose a skill.
                            <em className="text-[#D5AD57]"> Build a career.</em>
                        </h2>
                    </div>
                    <p className="text-base leading-8 text-white/65">
                        A flexible admission model for vocational degree, diploma and certificate programmes.
                    </p>
                </header>

                <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {defaultSteps.map(([n, t, d]) => (
                        <article key={n} className="rounded-[24px] border border-white/15 bg-white/[0.05] p-6 backdrop-blur">
                            <span className="text-sm font-bold text-[#D5AD57]">{n}</span>
                            <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-3xl">{t}</h3>
                            <p className="mt-4 text-sm leading-7 text-white/60">{d}</p>
                        </article>
                    ))}
                </div>

                <div className="mt-10 grid gap-5 lg:grid-cols-[1fr_340px]">
                    <div className="grid gap-5 sm:grid-cols-3">
                        {[
                            ["B.Voc", eligibilityBase.ug],
                            ["Diploma", eligibilityBase.diploma],
                            ["Certificate", "Entry requirements vary by skill area and current programme regulations."],
                        ].map(([title, text]) => (
                            <div key={title} className="rounded-[22px] bg-white p-6 text-[#132337]">
                                <h3 className="text-xl font-bold text-[#071A2F]">{title}</h3>
                                <p className="mt-3 text-sm leading-7 text-[#65717D]">{text}</p>
                            </div>
                        ))}
                    </div>
                    <aside className="rounded-[22px] bg-[#D5AD57] p-7 text-[#071A2F]">
                        <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-3xl">Need programme guidance?</h3>
                        <p className="mt-4 text-sm leading-7">Speak with an advisor to select the right vocational pathway.</p>
                        <a href="/contact-us" className="mt-6 inline-flex min-h-[48px] items-center gap-3 rounded-full bg-[#071A2F] px-6 text-sm font-bold text-white">
                            Contact Advisor <ArrowUpRight />
                        </a>
                    </aside>
                </div>
            </div>
        </section>
    );
}

/* 13. HEALTH & ALLIED SCIENCE */
export function SchoolOfHealthAlliedScienceAdmissionEligibility() {
    return (
        <section className="relative overflow-hidden bg-white py-20 text-[#132337] sm:py-24 lg:py-28">
            <div className="absolute right-0 top-0 hidden h-full w-[34%] bg-[#F4E8EC] lg:block" />
            <div className="relative mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                <header className="grid gap-8 border-b border-[#071A2F]/15 pb-10 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
                    <div>
                        <Badge>Admission Process & Eligibility</Badge>
                        <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04] text-[#071A2F]">
                            Begin a career dedicated to
                            <em className="text-[#8B1E3F]"> health and wellbeing.</em>
                        </h2>
                    </div>
                    <p className="text-base leading-8 text-[#65717D]">
                        Apply for programmes in physiotherapy, medical biotechnology, forensic science, nutrition, dietetics and public health.
                    </p>
                </header>

                <div className="mt-12 grid gap-7 lg:grid-cols-[.8fr_1.2fr]">
                    <aside className="rounded-[30px] bg-[#071A2F] p-8 text-white">
                        <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#D5AD57]">Eligibility Overview</span>
                        {[
                            ["Bachelor’s Programmes", eligibilityBase.ug],
                            ["Master’s Programmes", eligibilityBase.pg],
                            ["PG Diploma", "Relevant bachelor’s qualification under current programme requirements."],
                            ["Diploma", eligibilityBase.diploma],
                        ].map(([title, text]) => (
                            <div key={title} className="border-b border-white/15 py-5">
                                <h3 className="text-lg font-bold">{title}</h3>
                                <p className="mt-2 text-sm leading-7 text-white/60">{text}</p>
                            </div>
                        ))}
                        <div className="mt-7"><CTA light /></div>
                    </aside>

                    <div className="grid gap-5 sm:grid-cols-2">
                        {defaultSteps.map(([n, t, d]) => (
                            <article key={n} className="rounded-[28px] border border-[#071A2F]/10 bg-[#F7F2E8] p-7 transition hover:-translate-y-1 hover:bg-white hover:shadow-[0_24px_60px_rgba(7,26,47,.10)]">
                                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#8B1E3F] text-sm font-bold text-white">{n}</span>
                                <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-3xl text-[#071A2F]">{t}</h3>
                                <p className="mt-4 text-sm leading-7 text-[#65717D]">{d}</p>
                            </article>
                        ))}
                    </div>
                </div>

                <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {documents.map((item, index) => (
                        <div key={item} className="flex items-center gap-4 rounded-[18px] border border-[#071A2F]/10 bg-white p-5">
                            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#F4E8EC] text-sm font-bold text-[#8B1E3F]">0{index + 1}</span>
                            <span className="text-sm font-bold text-[#071A2F]">{item}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

const coreProgrammeEligibility = [
    ["UG", "Undergraduate", eligibilityBase.ug],
    ["PG", "Postgraduate", eligibilityBase.pg],
    ["DP", "Diploma", eligibilityBase.diploma],
    ["PhD", "Doctoral", eligibilityBase.phd],
];

/* 14. MANAGEMENT — exact supplied design */
export function SchoolOfManagementAdmissionEligibility() {
    const admissionSteps = [
        {
            number: "01",
            title: "Choose your programme",
            text: "Explore programme structure, duration, specialisations and career pathways.",
        },
        {
            number: "02",
            title: "Submit application",
            text: "Complete the online form with accurate personal and academic information.",
        },
        {
            number: "03",
            title: "Upload documents",
            text: "Provide the required academic records, identification and recent photograph.",
        },
        {
            number: "04",
            title: "Eligibility review",
            text: "The admissions team verifies the application against current programme norms.",
        },
        {
            number: "05",
            title: "Confirm admission",
            text: "Eligible applicants receive further instructions to complete enrolment.",
        },
    ];
    const programmes2 = [
        {
            id: "phd-management",
            shortName: "Ph.D.",
            name: "Doctor of Philosophy in Management",
            level: "Doctoral",
            duration: "3 Years",
            qualification: "Relevant postgraduate qualification",
            eligibility:
                "Candidates should hold an eligible postgraduate qualification and meet the requirements prescribed under current University and regulatory norms.",
            selection:
                "Admission is subject to the applicable entrance, interview, research proposal and regulatory requirements.",
            idealFor:
                "Undertake advanced research in business, management, entrepreneurship, finance, marketing and organizational studies under experienced faculty guidance.",
        },
        {
            id: "mba",
            shortName: "MBA",
            name: "Master of Business Administration",
            level: "Postgraduate",
            duration: "2 Years",
            qualification: "Bachelor's degree in any discipline",
            eligibility:
                "Candidates should hold a Bachelor’s degree in any recognised discipline and satisfy the minimum marks prescribed by the University.",
            selection:
                "Selection may consider academic records and any additional interaction or assessment required under current admission guidelines.",
            idealFor:
                "Develop leadership, strategic thinking and managerial expertise through industry-oriented learning in finance, marketing, HR, operations and business analytics.",
        },
        {
            id: "emba",
            shortName: "EMBA",
            name: "Executive Master of Business Administration",
            level: "Postgraduate",
            duration: "18 Months",
            qualification: "Bachelor's degree with relevant work experience",
            eligibility:
                "Candidates should hold a recognised Bachelor’s degree together with relevant professional experience.",
            selection:
                "The application may be reviewed against academic background, professional experience and current programme requirements.",
            idealFor:
                "Designed for working professionals to strengthen leadership capabilities, strategic decision-making and business management skills while continuing their careers.",
        },
        {
            id: "bba",
            shortName: "BBA",
            name: "Bachelor of Business Administration",
            level: "Undergraduate",
            duration: "3 Years",
            qualification: "10+2 or Equivalent",
            eligibility:
                "Candidates should have successfully completed 10+2 or an equivalent examination from a recognised board.",
            selection:
                "Admission is considered through academic eligibility and the University’s prevailing admission process.",
            idealFor:
                "Build a strong foundation in business management, entrepreneurship, marketing, finance, human resources and organizational behaviour through practical learning.",
        },
        {
            id: "pgdba",
            shortName: "PGDBA",
            name: "Post Graduate Diploma in Business Administration",
            level: "Postgraduate Diploma",
            duration: "1 Year",
            qualification: "Bachelor's degree in any discipline",
            eligibility:
                "Candidates should hold a Bachelor’s degree in any discipline from a recognised institution.",
            selection:
                "Admission is considered through academic eligibility and the University’s prevailing admission process.",
            idealFor:
                "Gain industry-focused managerial knowledge with practical exposure to business operations, strategic planning and organizational leadership.",
        },
        {
            id: "pgdbm",
            shortName: "PGDBM",
            name: "Post Graduate Diploma in Business Management",
            level: "Postgraduate Diploma",
            duration: "2 Years",
            qualification: "Bachelor's degree in any discipline",
            eligibility:
                "Candidates should hold a Bachelor’s degree in any discipline from a recognised institution.",
            selection:
                "Admission is considered through academic eligibility and the University’s prevailing admission process.",
            idealFor:
                "Enhance professional skills in management, finance, marketing, operations and business communication for successful corporate careers.",
        },
        {
            id: "pgdm",
            shortName: "PGDM",
            name: "Postgraduate Diploma in Management",
            level: "Postgraduate Diploma",
            duration: "2 Years",
            qualification: "Bachelor's degree in any discipline",
            eligibility:
                "Candidates should hold a Bachelor’s degree in any discipline from a recognised institution.",
            selection:
                "Selection may consider academic records and any additional interaction or assessment required under current admission guidelines.",
            idealFor:
                "Prepare for leadership roles through an industry-driven curriculum covering business strategy, analytics, innovation and organizational management.",
        },
        {
            id: "dba",
            shortName: "DBA",
            name: "Diploma in Business Administration",
            level: "Diploma",
            duration: "1 Year",
            qualification: "10+2 or Equivalent",
            eligibility:
                "Candidates should have successfully completed 10+2 or an equivalent examination from a recognised board.",
            selection:
                "Admission is considered through academic eligibility and the University’s prevailing admission process.",
            idealFor:
                "Acquire practical knowledge of business administration, office management, accounting fundamentals and customer relationship management for entry-level managerial roles.",
        },
    ];
    const documents = [
        "Academic mark sheets",
        "Qualifying certificate",
        "Government identity proof",
        "Recent passport photograph",
        "Transfer or migration document",
        "Category certificate, if applicable",
    ];
    const [activeProgramme, setActiveProgramme] = useState(programmes2[0]);
    return (
        <>
            {/* Admission Process & Eligibility */}
            <section className="aes" aria-labelledby="admission-eligibility-title">
                <div className="aes__intro">
                    <div className="aes__container aes__intro-grid">
                        <div>
                            <span className="aes__eyebrow">Admissions 2026</span>
                            <h2 id="admission-eligibility-title">
                                Your journey to the School of Management
                                <em> starts here.</em>
                            </h2>
                        </div>

                        <div className="aes__intro-side">
                            <p>
                                A transparent admission pathway helps you understand the process,
                                prepare the right documents and identify the management programme
                                that matches your academic and professional goals.
                            </p>
                            <div className="aes__intro-actions">
                                <a href="/apply-online">Apply online <span>↗</span></a>
                                <a href="/admissions">Admission help <span>→</span></a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="aes__process">
                    <div className="aes__container">
                        <div className="aes__process-head">
                            <span>Application pathway</span>
                            <small>Five clear steps</small>
                        </div>

                        <div className="aes__step-list">
                            {admissionSteps.map((step, index) => (
                                <article className="aes__step" key={step.number}>
                                    <div className="aes__step-top">
                                        <span>{step.number}</span>
                                        <i aria-hidden="true">{index < admissionSteps.length - 1 ? "→" : "✓"}</i>
                                    </div>
                                    <h3>{step.title}</h3>
                                    <p>{step.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="aes__eligibility">
                    <div className="aes__container">
                        <header className="aes__section-head">
                            <div>
                                <span className="aes__eyebrow">Programme eligibility</span>
                                <h2>Find the programme that fits your next step.</h2>
                            </div>
                            <p>
                                Select a programme to review indicative academic eligibility,
                                programme level and selection information.
                            </p>
                        </header>

                        <div className="aes__eligibility-shell">
                            <div className="aes__programme-tabs" role="tablist" aria-label="Management programmes">
                                {programmes2.map((programme, index) => (
                                    <button type="button" role="tab" aria-selected={activeProgramme.id === programme.id}
                                        aria-controls="management-eligibility-panel" className={activeProgramme.id === programme.id
                                            ? "is-active" : ""} onClick={() => setActiveProgramme(programme)}
                                        key={programme.id}
                                    >
                                        <small>{String(index + 1).padStart(2, "0")}</small>
                                        <span>{programme.shortName}</span>
                                        <i>→</i>
                                    </button>
                                ))}
                            </div>

                            <article className="aes__programme-panel" id="management-eligibility-panel" role="tabpanel"
                                aria-live="polite" key={activeProgramme.id}>
                                <div className="aes__programme-title">
                                    <span>{activeProgramme.level} programme</span>
                                    <h3>{activeProgramme.name}</h3>
                                </div>

                                <div className="aes__programme-facts">
                                    <div>
                                        <small>Duration</small>
                                        <strong>{activeProgramme.duration}</strong>
                                    </div>
                                    <div>
                                        <small>Entry qualification</small>
                                        <strong>{activeProgramme.qualification}</strong>
                                    </div>
                                </div>

                                <div className="aes__programme-details">
                                    <div>
                                        <span>Eligibility guidance</span>
                                        <p>{activeProgramme.eligibility}</p>
                                    </div>
                                    <div>
                                        <span>Selection process</span>
                                        <p>{activeProgramme.selection}</p>
                                    </div>
                                    <div>
                                        <span>Ideal for</span>
                                        <p>{activeProgramme.idealFor}</p>
                                    </div>
                                </div>

                                <div className="aes__programme-bottom">
                                    <p>
                                        Final eligibility is subject to verification and the
                                        University’s current admission regulations.
                                    </p>
                                    <a href={`/programmes/${activeProgramme.id}`}>
                                        View programme details <span>↗</span>
                                    </a>
                                </div>
                            </article>
                        </div>
                    </div>
                </div>

                <div className="aes__support">
                    <div className="aes__container aes__support-grid">
                        <div className="aes__documents">
                            <div className="aes__support-title">
                                <span>Application checklist</span>
                                <h2>Keep these documents ready.</h2>
                            </div>

                            <div className="aes__document-list">
                                {documents.map((document, index) => (
                                    <div key={document}>
                                        <span>0{index + 1}</span>
                                        <strong>{document}</strong>
                                        <i aria-hidden="true">✓</i>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <aside className="aes__help">
                            <span className="aes__help-icon" aria-hidden="true">?</span>
                            <small>Need admission assistance?</small>
                            <h3>Speak with our admissions team.</h3>
                            <p>
                                Get support with programme selection, eligibility, documents and
                                the online application process.
                            </p>
                            <a href="tel:+919373199999">
                                Call +91-9373199999 <span>↗</span>
                            </a>
                            <a href="/request-callback">
                                Request a callback <span>→</span>
                            </a>
                        </aside>
                    </div>
                </div>
            </section>
        </>
    );
}

/* 15. COMMERCE */
export function SchoolOfCommerceAdmissionEligibility() {
    return <section className="bg-[#F4E8EC] py-20 sm:py-24 lg:py-28"><div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]"><header className="border-b-2 border-[#071A2F] pb-10"><Badge>Admission process & eligibility</Badge><h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04] text-[#071A2F]">A clear route into <em className="font-normal text-[#8B1E3F]">commerce education.</em></h2></header><div className="mt-12 grid border-2 border-[#071A2F] lg:grid-cols-[1fr_420px]"><div className="bg-white p-7 sm:p-10"><span className="text-sm font-bold uppercase tracking-[.14em] text-[#8B1E3F]">Application workflow</span><div className="mt-7 grid md:grid-cols-2">{defaultSteps.map(([n, t, d]) => <article key={n} className="grid min-h-44 grid-cols-[48px_1fr] border-b border-r border-[#071A2F]/15 p-5"><span className="text-sm font-bold text-[#A77B2D]">{n}</span><div><h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-2xl text-[#071A2F]">{t}</h3><p className="mt-3 text-sm leading-7 text-[#65717D]">{d}</p></div></article>)}</div></div><aside className="bg-[#071A2F] p-7 text-white sm:p-9"><span className="text-sm font-bold uppercase tracking-[.14em] text-[#D5AD57]">Eligibility register</span><div className="mt-7 border-t border-white/20">{coreProgrammeEligibility.map(([code, level, text], index) => <div key={code} className="border-b border-white/20 py-6"><div className="flex justify-between"><h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-2xl">{level}</h3><span className="text-sm font-bold text-[#D5AD57]">0{index + 1}</span></div><p className="mt-3 text-sm leading-7 text-white/60">{text}</p></div>)}</div><div className="mt-8"><CTA light /></div></aside></div></div></section>;
}

const engineeringSteps = defaultSteps.map(([number, title, description]) => ({ number, title, description, checklist: ["Review programme information", "Prepare academic records", "Follow admission guidance"] }));

/* 16. ENGINEERING & TECHNOLOGY — exact supplied design */
export function SchoolOfEngineeringTechnologyAdmissionEligibility() {
    const engineeringAdmissionSteps = [
        {
            number: "01",
            title: "Choose Your Programme",
            short: "Programme discovery",
            description:
                "Explore engineering disciplines, programme structures, eligibility requirements and potential career pathways before selecting your programme.",
            checklist: [
                "Explore engineering disciplines",
                "Review programme curriculum",
                "Check eligibility requirements",
            ],
        },
        {
            number: "02",
            title: "Online Application",
            short: "Digital application",
            description:
                "Complete the university application form with your personal, academic and programme-related information.",
            checklist: [
                "Create applicant profile",
                "Enter academic information",
                "Select preferred programme",
            ],
        },
        {
            number: "03",
            title: "Upload Documents",
            short: "Document submission",
            description:
                "Upload the required academic records and identification documents through the online admission interface.",
            checklist: [
                "Academic mark sheets",
                "Identity documents",
                "Recent photograph",
            ],
        },
        {
            number: "04",
            title: "Eligibility Review",
            short: "Academic evaluation",
            description:
                "The admissions team reviews your qualifications according to the eligibility requirements of the selected engineering programme.",
            checklist: [
                "Qualification verification",
                "Programme eligibility",
                "Applicant guidance",
            ],
        },
        {
            number: "05",
            title: "Confirm Admission",
            short: "Admission decision",
            description:
                "Eligible applicants receive admission confirmation with enrolment information and the applicable fee details.",
            checklist: [
                "Receive admission confirmation",
                "Review enrolment details",
                "Complete seat confirmation",
            ],
        },
        {
            number: "06",
            title: "Student Onboarding",
            short: "Begin your journey",
            description:
                "Complete fee payment and receive orientation, academic access and information required to begin your engineering programme.",
            checklist: [
                "Complete fee payment",
                "Attend orientation",
                "Access student resources",
            ],
        },
    ];
    const engineeringEligibility = [
        {
            id: "undergraduate",
            code: "UG",
            title: "Undergraduate Engineering",
            programmes: "B.Tech and related undergraduate programmes",
            qualification:
                "10+2 or equivalent qualification with relevant subjects as prescribed for the selected programme.",
            duration: "Programme specific",
            note: "Programme-specific subject and minimum-mark requirements may apply.",
        },
        {
            id: "postgraduate",
            code: "PG",
            title: "Postgraduate Engineering",
            programmes: "M.Tech and related postgraduate programmes",
            qualification:
                "Relevant bachelor’s degree in Engineering, Technology or an accepted allied discipline.",
            duration: "Programme specific",
            note: "Specialisation-specific academic requirements may apply.",
        },
        {
            id: "diploma",
            code: "DP",
            title: "Diploma in Engineering",
            programmes: "Engineering diploma programmes",
            qualification:
                "10th or an equivalent recognised qualification, subject to programme requirements.",
            duration: "Programme specific",
            note: "Lateral-entry conditions should be verified separately.",
        },
        {
            id: "doctoral",
            code: "PhD",
            title: "Doctoral Research",
            programmes: "Ph.D. in Engineering and Technology disciplines",
            qualification:
                "Relevant postgraduate qualification subject to university doctoral admission regulations.",
            duration: "As per regulations",
            note: "Research entrance and interview requirements may apply.",
        },
    ];
    const [activeAdmissionStep, setActiveAdmissionStep] = useState(0);
    const [activeEligibility, setActiveEligibility] = useState('undergraduate');
    const selectedAdmissionStep = engineeringAdmissionSteps[activeAdmissionStep];
    const selectedEligibility = engineeringEligibility.find((item) => item.id === activeEligibility);
    return (
        <>
            {/* Admission Process & Eligibility */}
            <section className="relative overflow-hidden bg-[#F7F2E8] py-20 sm:py-24 lg:py-32"
                aria-labelledby="engineering-admission-heading">
                {/* Background */}
                <div className="pointer-events-none absolute inset-0">
                    <div className="absolute -right-52 -top-44 h-[580px] w-[580px] rounded-full bg-[#D5AD57]/20 blur-[120px]" />

                    <div
                        className="absolute -bottom-56 -left-48 h-[560px] w-[560px] rounded-full bg-[#8B1E3F]/[0.065] blur-[120px]" />

                    <div className="absolute inset-0 opacity-[0.025]" style={{
                        backgroundImage: "radial-gradient(#071A2F 1px, transparent 1px)", backgroundSize: "28px 28px",
                    }} />
                </div>

                <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                    {/* Heading */}
                    <div className="grid gap-8 lg:grid-cols-[1fr_0.6fr] lg:items-end">
                        <div>
                            <div className="flex items-center gap-4">
                                <span className="h-px w-10 bg-[#8B1E3F]" />

                                <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#8B1E3F]">
                                    Admission process &amp; eligibility
                                </span>
                            </div>

                            <h2 id="engineering-admission-heading"
                                className="mt-6 max-w-4xl text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.045em] text-[#071A2F]"
                                style={{ fontFamily: '"Libre Caslon Display", Georgia, serif', }}>
                                Your pathway into
                                <span className="italic text-[#8B1E3F]">
                                    {" "}
                                    engineering education.
                                </span>
                            </h2>
                        </div>

                        <p className="max-w-xl text-base leading-8 text-[#132337]/65 lg:pb-2">
                            Follow a clear, guided admission journey—from selecting your
                            engineering discipline to joining the ISBM academic community.
                        </p>
                    </div>

                    {/* Admission console */}
                    <div
                        className="mt-14 overflow-hidden rounded-[34px] border border-[#071A2F]/10 bg-white shadow-[0_30px_90px_rgba(7,26,47,0.10)]">
                        {/* Progress header */}
                        <div className="overflow-x-auto border-b border-[#071A2F]/10">
                            <div className="relative flex min-w-[880px]" role="tablist" aria-label="Engineering admission process">
                                {engineeringAdmissionSteps.map((step, index) => {
                                    const isActive = activeAdmissionStep === index;
                                    const isComplete = index < activeAdmissionStep; return (<button key={step.number} type="button"
                                        role="tab" aria-selected={isActive} onClick={() => setActiveAdmissionStep(index)}
                                        className={`group relative flex min-h-[120px] flex-1 flex-col items-start justify-center
                                    border-r border-[#071A2F]/10 px-5 text-left transition duration-300 last:border-r-0 ${isActive
                                                ? "bg-[#071A2F] text-white"
                                                : "bg-white text-[#071A2F] hover:bg-[#F7F2E8]"
                                            }`}
                                    >
                                        <span className={`flex h-9 w-9 items-center justify-center rounded-full border text-[10px]
                                        font-bold transition ${isActive ? "border-[#D5AD57] bg-[#D5AD57] text-[#071A2F]" :
                                                isComplete ? "border-[#8B1E3F] bg-[#8B1E3F] text-white"
                                                    : "border-[#071A2F]/15 bg-white text-[#071A2F]"}`}>
                                            {isComplete ? "✓" : step.number}
                                        </span>

                                        <strong className="mt-3 text-xs font-semibold">
                                            {step.title}
                                        </strong>

                                        {isActive && (
                                            <span className="absolute bottom-0 left-0 h-[3px] w-full bg-[#D5AD57]" />
                                        )}
                                    </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Active step */}
                        <div className="grid lg:grid-cols-[0.72fr_1.28fr]">
                            <div className="relative overflow-hidden bg-[#071A2F] p-8 text-white sm:p-10 lg:p-12">
                                <span
                                    className="absolute -right-3 -top-10 select-none text-[180px] font-normal leading-none text-white/[0.035]"
                                    style={{ fontFamily: '"Libre Caslon Display", Georgia, serif', }}>
                                    {selectedAdmissionStep.number}
                                </span>

                                <div className="relative">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D5AD57]">
                                        Current admission stage
                                    </span>

                                    <h3 className="mt-5 text-4xl font-normal leading-tight" style={{
                                        fontFamily: '"Libre Caslon Display", Georgia, serif',
                                    }}>
                                        {selectedAdmissionStep.title}
                                    </h3>

                                    <p className="mt-5 text-sm leading-7 text-white/60">
                                        {selectedAdmissionStep.description}
                                    </p>

                                    <div className="mt-9">
                                        <div
                                            className="flex items-center justify-between text-[9px] font-bold uppercase tracking-[0.14em] text-white/35">
                                            <span>Application progress</span>
                                            <span>
                                                {activeAdmissionStep + 1}/
                                                {engineeringAdmissionSteps.length}
                                            </span>
                                        </div>

                                        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
                                            <div className="h-full rounded-full bg-gradient-to-r from-[#8B1E3F] to-[#D5AD57] transition-all duration-500"
                                                style={{
                                                    width: `${((activeAdmissionStep + 1) / engineeringAdmissionSteps.length) *
                                                        100}%`,
                                                }} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-8 sm:p-10 lg:p-12">
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                    {selectedAdmissionStep.short}
                                </span>

                                <div className="mt-7 grid gap-4 sm:grid-cols-3">
                                    {selectedAdmissionStep.checklist.map((item, index) => (
                                        <div key={item} className="rounded-2xl border border-[#071A2F]/10 bg-[#F7F2E8]/70 p-5">
                                            <span
                                                className="flex h-9 w-9 items-center justify-center rounded-full bg-[#8B1E3F] text-[10px] font-bold text-white">
                                                0{index + 1}
                                            </span>

                                            <strong className="mt-4 block text-sm leading-6 text-[#071A2F]">
                                                {item}
                                            </strong>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 flex flex-wrap gap-3">
                                    <button type="button" disabled={activeAdmissionStep === 0} onClick={() =>
                                        setActiveAdmissionStep((current) =>
                                            Math.max(0, current - 1)
                                        )
                                    }
                                        className="rounded-full border border-[#071A2F]/15 px-6 py-3 text-xs font-bold
                                        text-[#071A2F] transition hover:border-[#8B1E3F] hover:text-[#8B1E3F]
                                        disabled:cursor-not-allowed disabled:opacity-30"
                                    >
                                        Previous stage
                                    </button>

                                    <button type="button" disabled={activeAdmissionStep === engineeringAdmissionSteps.length - 1}
                                        onClick={() =>
                                            setActiveAdmissionStep((current) =>
                                                Math.min(
                                                    engineeringAdmissionSteps.length - 1,
                                                    current + 1
                                                )
                                            )
                                        }
                                        className="inline-flex items-center gap-3 rounded-full bg-[#8B1E3F] px-6 py-3 text-xs
                                        font-bold text-white transition hover:-translate-y-1 hover:bg-[#071A2F]
                                        disabled:cursor-not-allowed disabled:opacity-30"
                                    >
                                        Next stage
                                        <span aria-hidden="true">→</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Eligibility interface */}
                    <div className="mt-16">
                        <div>
                            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                Entry requirements
                            </span>

                            <h3 className="mt-3 text-3xl font-normal text-[#071A2F] sm:text-4xl" style={{
                                fontFamily: '"Libre Caslon Display", Georgia, serif',
                            }}>
                                Select your academic level.
                            </h3>
                        </div>

                        <div className="mt-8 grid gap-6 lg:grid-cols-[0.42fr_1.58fr]">
                            {/* Eligibility tabs */}
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1" role="tablist"
                                aria-label="Engineering eligibility levels">
                                {engineeringEligibility.map((item) => {
                                    const isActive = activeEligibility === item.id;

                                    return (
                                        <button key={item.id} type="button" role="tab" aria-selected={isActive} onClick={() =>
                                            setActiveEligibility(item.id)}
                                            className={`group flex items-center gap-4 rounded-2xl border p-4 text-left transition
                                    duration-300 ${isActive
                                                    ? "border-[#071A2F] bg-[#071A2F] text-white shadow-lg"
                                                    : "border-[#071A2F]/10 bg-white text-[#071A2F] hover:border-[#D5AD57]"
                                                }`}
                                        >
                                            <span className={`flex h-11 min-w-11 items-center justify-center rounded-xl px-2 text-[10px]
                                        font-bold ${isActive ? "bg-[#D5AD57] text-[#071A2F]" : "bg-[#F7F2E8] text-[#8B1E3F]"}`}>
                                                {item.code}
                                            </span>

                                            <span className="min-w-0">
                                                <small className={`block text-[9px] font-bold uppercase tracking-[0.14em] ${isActive
                                                    ? "text-[#D5AD57]" : "text-[#8B1E3F]"}`}>
                                                    Academic pathway
                                                </small>

                                                <strong className="mt-1 block text-sm">
                                                    {item.title}
                                                </strong>
                                            </span>

                                            <span className={`ml-auto transition group-hover:translate-x-1 ${isActive ? "text-[#D5AD57]"
                                                : "text-[#071A2F]/30"}`} aria-hidden="true">
                                                →
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Selected eligibility */}
                            <div className="overflow-hidden rounded-[28px] border border-[#071A2F]/10 bg-white shadow-[0_20px_60px_rgba(7,26,47,0.08)]"
                                role="tabpanel">
                                <div className="grid h-full md:grid-cols-[0.72fr_1.28fr]">
                                    <div className="relative overflow-hidden bg-[#8B1E3F] p-7 text-white sm:p-8">
                                        <span
                                            className="absolute -bottom-8 -right-3 select-none text-[150px] font-normal leading-none text-white/[0.06]"
                                            style={{ fontFamily: '"Libre Caslon Display", Georgia, serif', }}>
                                            {selectedEligibility.code}
                                        </span>

                                        <div className="relative">
                                            <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                                Programme category
                                            </span>

                                            <h4 className="mt-5 text-3xl font-normal leading-tight" style={{
                                                fontFamily: '"Libre Caslon Display", Georgia, serif',
                                            }}>
                                                {selectedEligibility.title}
                                            </h4>

                                            <p className="mt-5 text-sm leading-7 text-white/65">
                                                {selectedEligibility.programmes}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="p-7 sm:p-8">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8B1E3F]">
                                            Minimum qualification
                                        </span>

                                        <p className="mt-4 text-2xl font-normal leading-relaxed text-[#071A2F]" style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}>
                                            {selectedEligibility.qualification}
                                        </p>

                                        <div className="mt-7 grid gap-4 border-t border-[#071A2F]/10 pt-6 sm:grid-cols-2">
                                            <div>
                                                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                                                    Duration
                                                </span>

                                                <strong className="mt-2 block text-sm text-[#071A2F]">
                                                    {selectedEligibility.duration}
                                                </strong>
                                            </div>

                                            <div>
                                                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                                                    Important
                                                </span>

                                                <p className="mt-2 text-xs leading-5 text-[#132337]/55">
                                                    {selectedEligibility.note}
                                                </p>
                                            </div>
                                        </div>

                                        <a href="/programmes"
                                            className="mt-7 inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                                            View eligible programmes
                                            <span aria-hidden="true">→</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Admission CTA */}
                    <div
                        className="mt-10 flex flex-col gap-6 rounded-[26px] bg-[#071A2F] p-7 text-white sm:flex-row sm:items-center sm:justify-between sm:p-9">
                        <div>
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                Admissions support
                            </span>

                            <p className="mt-2 text-2xl font-normal" style={{
                                fontFamily: '"Libre Caslon Display", Georgia, serif',
                            }}>
                                Need help selecting the right engineering programme?
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <a href="/contact-us"
                                className="rounded-full border border-white/20 px-6 py-3 text-xs font-bold text-white transition hover:bg-white hover:text-[#071A2F]">
                                Speak to admissions
                            </a>

                            <a href="/apply-online"
                                className="rounded-full bg-[#D5AD57] px-6 py-3 text-xs font-bold text-[#071A2F] transition hover:-translate-y-1 hover:bg-white">
                                Apply online
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

/* 17. INFORMATION & TECHNOLOGY — exact supplied design */
export function SchoolOfInformationTechnologyAdmissionEligibility() {
    const admissionSteps = [
        {
            number: "01",
            title: "Online Application",
            label: "Begin your journey",
            description:
                "Complete the digital application form with your personal, academic and programme information.",
            items: ["Select programme", "Enter academic details", "Submit application"],
        },
        {
            number: "02",
            title: "Document Verification",
            label: "Secure document review",
            description:
                "Upload the required academic and identification documents through the admission interface.",
            items: ["Academic records", "Identity documents", "Recent photograph"],
        },
        {
            number: "03",
            title: "Eligibility Review",
            label: "Academic assessment",
            description:
                "The admissions team evaluates your qualifications according to the eligibility requirements of the selected programme.",
            items: ["Qualification review", "Programme eligibility", "Admission guidance"],
        },
        {
            number: "04",
            title: "Admission Confirmation",
            label: "Receive your offer",
            description:
                "Eligible applicants receive admission confirmation along with programme and enrolment information.",
            items: ["Admission decision", "Offer confirmation", "Enrolment details"],
        },
        {
            number: "05",
            title: "Fee Payment",
            label: "Confirm your seat",
            description:
                "Complete the prescribed fee payment securely using the available university payment channels.",
            items: ["Fee information", "Secure payment", "Payment acknowledgement"],
        },
        {
            number: "06",
            title: "Student Onboarding",
            label: "Enter the ISBM community",
            description:
                "Access orientation information, academic support systems and the resources needed to begin your programme.",
            items: ["Student orientation", "Academic access", "Campus introduction"],
        },
    ];
    const eligibilityCards = [
        {
            title: "Undergraduate programmes",
            code: "UG",
            eligibility: "10+2 or equivalent qualification",
            note: "Programme-specific eligibility conditions may apply.",
        },
        {
            title: "Postgraduate programmes",
            code: "PG",
            eligibility: "Bachelor’s degree in a relevant or accepted discipline",
            note: "Requirements vary according to the selected programme.",
        },
        {
            title: "Diploma programmes",
            code: "DP",
            eligibility: "10th or 10+2 depending on the programme",
            note: "Refer to the programme page for complete requirements.",
        },
        {
            title: "Doctoral programmes",
            code: "PhD",
            eligibility: "Relevant postgraduate qualification",
            note: "Subject to university research admission requirements.",
        },
    ];
    const [activeAdmissionStep, setActiveAdmissionStep] = useState(0);
    return (
        <>
            {/* ADMISSION PROCESS & ELIGIBILITY */}
            <section className="relative overflow-hidden bg-[#8B1E3F]/[0.055] py-20 sm:py-24 lg:py-32"
                aria-labelledby="admission-process-heading">
                <div className="pointer-events-none absolute inset-0 opacity-[0.025]" style={{
                    backgroundImage: "radial-gradient(#071A2F 1px, transparent 1px)", backgroundSize: "26px 26px",
                }} />

                <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                    <div className="max-w-4xl">
                        <SectionEyebrow>Admissions & eligibility</SectionEyebrow>

                        <h2 id="admission-process-heading"
                            className="mt-6 text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.045em] text-[#071A2F]"
                            style={{ fontFamily: '"Libre Caslon Display", Georgia, serif', }}>
                            A clear digital pathway to
                            <span className="italic text-[#8B1E3F]">
                                {" "}
                                your future in technology.
                            </span>
                        </h2>

                        <p className="mt-7 max-w-2xl text-base leading-8 text-[#132337]/65">
                            Move through a simple, guided admission experience—from your
                            first application to becoming part of the ISBM community.
                        </p>
                    </div>

                    <div
                        className="mt-14 overflow-hidden rounded-[32px] border border-[#071A2F]/10 bg-white shadow-[0_30px_90px_rgba(7,26,47,0.10)]">
                        {/* Progress navigation */}
                        <div className="overflow-x-auto border-b border-[#071A2F]/10" role="tablist" aria-label="Admission process">
                            <div className="flex min-w-[800px]">
                                {admissionSteps.map((step, index) => {
                                    const isActive = activeAdmissionStep === index;
                                    const isComplete = index < activeAdmissionStep; return (<button key={step.number} type="button"
                                        role="tab" aria-selected={isActive} onClick={() => setActiveAdmissionStep(index)}
                                        className={`relative flex flex-1 flex-col items-start px-5 py-5 text-left transition
                                    duration-300 ${isActive
                                                ? "bg-[#071A2F] text-white"
                                                : "hover:bg-[#F7F2E8]"
                                            }`}
                                    >
                                        <span className={`flex h-8 w-8 items-center justify-center rounded-full border text-[10px]
                                        font-bold ${isActive ? "border-[#D5AD57] bg-[#D5AD57] text-[#071A2F]" : isComplete
                                                ? "border-[#8B1E3F] bg-[#8B1E3F] text-white" : "border-[#071A2F]/15 text-[#071A2F]"}`}>
                                            {isComplete ? "✓" : step.number}
                                        </span>

                                        <strong className="mt-3 text-xs font-semibold">
                                            {step.title}
                                        </strong>

                                        {index
                                            < admissionSteps.length - 1 && (<span
                                                className="absolute right-[-7px] top-9 z-10 hidden h-px w-4 bg-[#D5AD57] sm:block" />
                                            )}
                                    </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Active admission panel */}
                        <div className="grid lg:grid-cols-[0.75fr_1.25fr]">
                            <div className="relative overflow-hidden bg-[#071A2F] p-8 text-white sm:p-10 lg:p-12">
                                <span className="absolute right-5 top-0 select-none text-[130px] leading-none text-white/[0.04]"
                                    style={{ fontFamily: '"Libre Caslon Display", Georgia, serif', }}>
                                    {admissionSteps[activeAdmissionStep].number}
                                </span>

                                <div className="relative">
                                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D5AD57]">
                                        Current stage
                                    </span>

                                    <h3 className="mt-5 text-4xl font-normal leading-tight" style={{
                                        fontFamily: '"Libre Caslon Display", Georgia, serif',
                                    }}>
                                        {admissionSteps[activeAdmissionStep].title}
                                    </h3>

                                    <p className="mt-5 text-sm leading-7 text-white/60">
                                        {admissionSteps[activeAdmissionStep].description}
                                    </p>

                                    <div className="mt-9 h-1.5 overflow-hidden rounded-full bg-white/10">
                                        <div className="h-full rounded-full bg-gradient-to-r from-[#D5AD57] to-[#8B1E3F] transition-all duration-500"
                                            style={{ width: `${((activeAdmissionStep + 1) / admissionSteps.length) * 100}%`, }} />
                                    </div>

                                    <div
                                        className="mt-3 flex justify-between text-[10px] font-bold uppercase tracking-wider text-white/40">
                                        <span>Application</span>
                                        <span>Onboarding</span>
                                    </div>
                                </div>
                            </div>

                            <div className="p-8 sm:p-10 lg:p-12">
                                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                    {admissionSteps[activeAdmissionStep].label}
                                </span>

                                <div className="mt-7 grid gap-4">
                                    {admissionSteps[activeAdmissionStep].items.map(
                                        (item, index) => (
                                            <div key={item}
                                                className="flex items-center gap-5 rounded-2xl border border-[#071A2F]/10 bg-[#F7F2E8]/60 p-5">
                                                <span
                                                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#8B1E3F] text-xs font-bold text-white">
                                                    0{index + 1}
                                                </span>
                                                <strong className="text-sm text-[#071A2F] sm:text-base">
                                                    {item}
                                                </strong>
                                            </div>
                                        )
                                    )}
                                </div>

                                <div className="mt-8 flex flex-wrap gap-3">
                                    <button type="button" disabled={activeAdmissionStep === 0} onClick={() =>
                                        setActiveAdmissionStep((current) =>
                                            Math.max(0, current - 1)
                                        )
                                    }
                                        className="rounded-full border border-[#071A2F]/15 px-6 py-3 text-sm font-semibold
                                        text-[#071A2F] transition hover:border-[#8B1E3F] disabled:cursor-not-allowed
                                        disabled:opacity-30"
                                    >
                                        Previous
                                    </button>

                                    <button type="button" disabled={activeAdmissionStep === admissionSteps.length - 1} onClick={() =>
                                        setActiveAdmissionStep((current) =>
                                            Math.min(admissionSteps.length - 1, current + 1)
                                        )
                                    }
                                        className="inline-flex items-center gap-3 rounded-full bg-[#8B1E3F] px-6 py-3 text-sm
                                        font-semibold text-white transition hover:-translate-y-1 hover:bg-[#071A2F]
                                        disabled:cursor-not-allowed disabled:opacity-30"
                                    >
                                        Next stage
                                        <ArrowIcon />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Eligibility */}
                    <div className="mt-16">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                    Entry requirements
                                </span>
                                <h3 className="mt-3 text-3xl font-normal text-[#071A2F] sm:text-4xl" style={{
                                    fontFamily: '"Libre Caslon Display", Georgia, serif',
                                }}>
                                    Find your academic pathway.
                                </h3>
                            </div>

                            <a href="/programmes" className="inline-flex items-center gap-2 text-sm font-bold text-[#8B1E3F]">
                                View all programmes
                                <ArrowIcon />
                            </a>
                        </div>

                        <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                            {eligibilityCards.map((item) => (
                                <article key={item.code}
                                    className="group rounded-[24px] border border-[#071A2F]/10 bg-white p-6 transition duration-500 hover:-translate-y-2 hover:border-[#D5AD57] hover:shadow-[0_24px_55px_rgba(7,26,47,0.10)]">
                                    <span
                                        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl bg-[#071A2F] px-3 text-xs font-bold text-[#D5AD57]">
                                        {item.code}
                                    </span>

                                    <h4 className="mt-6 text-lg font-semibold text-[#071A2F]">
                                        {item.title}
                                    </h4>

                                    <p className="mt-4 text-sm font-medium leading-6 text-[#8B1E3F]">
                                        {item.eligibility}
                                    </p>

                                    <p className="mt-4 border-t border-[#071A2F]/10 pt-4 text-xs leading-5 text-[#132337]/50">
                                        {item.note}
                                    </p>
                                </article>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

/* 18. HOTEL MANAGEMENT */
export function SchoolOfHotelManagementAdmissionEligibility() {
    const hospitalityEligibility = [["Undergraduate", "Hotel management and hospitality programmes", eligibilityBase.ug], ["Postgraduate", "Advanced hospitality and management programmes", eligibilityBase.pg], ["Diploma", "Operational and skill-focused hospitality programmes", eligibilityBase.diploma], ["Doctoral", "Hospitality research programmes", eligibilityBase.phd]];
    return <section className="relative overflow-hidden bg-[#EEE5D3] py-20 sm:py-24 lg:py-28"><div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_10%,rgba(213,173,87,.18),transparent_28%)]" /><div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10"><header className="grid gap-9 lg:grid-cols-[1.12fr_.55fr] lg:items-end"><div><Badge>Admission process & eligibility</Badge><h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.06] text-[#071A2F]">Your journey into professional <em className="font-normal text-[#8B1E3F]">hospitality begins here.</em></h2></div><p className="border-l border-[#071A2F]/15 pl-7 text-base leading-8 text-[#65717D] max-lg:border-l-0 max-lg:border-t max-lg:pt-7">Understand each stage, prepare documents and select the programme aligned with your hospitality goals.</p></header><div className="relative mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-6"><div className="absolute left-[8%] right-[8%] top-7 hidden h-px bg-[#071A2F]/20 xl:block" />{defaultSteps.map(([n, t, d], index) => <article key={n} className="group relative rounded-[8px_28px_8px_8px] border border-[#071A2F]/10 bg-white/75 p-6 transition hover:-translate-y-1 hover:border-[#D5AD57]"><span className="relative z-10 grid h-14 w-14 place-items-center rounded-full border-4 border-[#EEE5D3] bg-[#071A2F] text-sm font-bold text-[#D5AD57] group-hover:bg-[#8B1E3F] group-hover:text-white">{n}</span><h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-xl text-[#071A2F]">{t}</h3><p className="mt-3 text-sm leading-7 text-[#65717D]">{d}</p><span className="absolute right-5 top-5 text-[#A77B2D]">{index < defaultSteps.length - 1 ? "→" : "✓"}</span></article>)}</div><div className="mt-16 border-b border-[#071A2F]/15 pb-7"><span className="text-sm font-bold uppercase tracking-[.14em] text-[#8B1E3F]">Programme eligibility</span><h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-4xl text-[#071A2F]">Choose the academic level that matches your next step.</h3></div><div className="mt-8 grid gap-5 md:grid-cols-2">{hospitalityEligibility.map(([level, programmesText, qualification], index) => <article key={level} className="grid overflow-hidden rounded-[8px_30px_8px_8px] border border-[#071A2F]/10 bg-white sm:grid-cols-[130px_1fr]"><div className="flex flex-col justify-between bg-[#071A2F] p-6 text-white"><span className="text-sm font-bold text-[#D5AD57]">0{index + 1}</span><strong className="mt-10 font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal">{level}</strong></div><div className="p-6"><span className="text-sm font-bold text-[#8B1E3F]">PROGRAMMES</span><h4 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl text-[#071A2F]">{programmesText}</h4><p className="mt-5 border-l-2 border-[#D5AD57] pl-4 text-sm leading-7 text-[#65717D]">{qualification}</p></div></article>)}</div><div className="mt-10 flex flex-col gap-5 rounded-[8px_30px_8px_8px] bg-[#8B1E3F] p-7 text-white sm:flex-row sm:items-center sm:justify-between"><h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-2xl">Start your application to the School of Hotel Management.</h3><CTA light /></div></div></section>;
}

/* DYNAMIC RENDERER */
export const admissionEligibilityComponents = {
    "school-of-management": SchoolOfManagementAdmissionEligibility,
    "school-of-commerce": SchoolOfCommerceAdmissionEligibility,
    "school-of-engineering-and-technology": SchoolOfEngineeringTechnologyAdmissionEligibility,
    "school-of-information-and-technology": SchoolOfInformationTechnologyAdmissionEligibility,
    "school-of-hotel-management": SchoolOfHotelManagementAdmissionEligibility,
    "school-of-science": SchoolOfScienceAdmissionEligibility,
    "school-of-arts-and-humanities": SchoolOfArtsHumanitiesAdmissionEligibility,
    "school-of-law": SchoolOfLawAdmissionEligibility,
    "school-of-pharmacy": SchoolOfPharmacyAdmissionEligibility,
    "school-of-design": SchoolOfDesignAdmissionEligibility,
    "school-of-education": SchoolOfEducationAdmissionEligibility,
    "school-of-journalism-and-mass-communication": SchoolOfJournalismMassCommunicationAdmissionEligibility,
    "school-of-media-and-entertainment": SchoolOfMediaEntertainmentAdmissionEligibility,
    "school-of-library-and-information-science": SchoolOfLibraryInformationScienceAdmissionEligibility,
    "school-of-yoga-and-naturopathy": SchoolOfYogaNaturopathyAdmissionEligibility,
    "school-of-life-science": SchoolOfLifeScienceAdmissionEligibility,
    "school-of-vocational-management": SchoolOfVocationalManagementAdmissionEligibility,
    "school-of-health-and-allied-science": SchoolOfHealthAlliedScienceAdmissionEligibility,
};

const schoolAliases = {
    "school-of-management": "management",
    "school-of-commerce": "commerce",
    "school-of-engineering-and-technology": "engineeringTechnology",
    "school-of-information-and-technology": "informationTechnology",
    "school-of-hotel-management": "hotelManagement",
    "school-of-science": "science",
    "school-of-arts-and-humanities": "artsHumanities",
    "school-of-law": "law",
    "school-of-pharmacy": "pharmacy",
    "school-of-design": "design",
    "school-of-education": "education",
    "school-of-journalism-and-mass-communication": "journalismMassCommunication",
    "school-of-media-and-entertainment": "mediaEntertainment",
    "school-of-library-and-information-science": "libraryInformationScience",
    "school-of-yoga-and-naturopathy": "yogaNaturopathy",
    "school-of-life-science": "lifeScience",
    "school-of-vocational-management": "vocationalManagement",
    "school-of-health-and-allied-science": "healthAlliedScience",
};

export function AdmissionEligibilityRenderer({ school }) {
    const schoolId = typeof school === "string" ? school : school?.id;
    const Component = useMemo(
        () => admissionEligibilityComponents[schoolAliases[schoolId] || schoolId] || SchoolOfScienceAdmissionEligibility,
        [schoolId]
    );

    return <Component />;
}
